/**
 * @file Hidden controls sandbox view used during controls-system development.
 */

import {
  destroyRegisteredControlsIn,
  getRegisteredControlValues,
  validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import {
  createDateControl,
} from "../../shared/ui/controls/dateControl.js";
import {
  createSelectControl,
} from "../../shared/ui/controls/selectControl.js";
import {
  createTextOptionControl,
} from "../../shared/ui/controls/textOptionControl.js";
import {
  createDateRangePreset,
  createPrioritySelectPreset,
  createProviderSelectPreset,
  createStatusSelectPreset,
  createTypeSelectPreset,
  createUserSelectPreset,
} from "../../shared/ui/controls/controlPresets.js";
import {
  applyBrandingVariables,
} from "../../shared/ui/brandingTokens.js";
import {
  renderMobileRouteHeaderActions,
} from "../../shared/ui/mobileRouteHeaderActions.js";
import {
  CURRENCY_INPUT_ALLOWED_PATTERN,
  restrictCurrencyInput,
  sanitizeCurrencyInputValue,
} from "../../shared/utils/currencyInput.js";
import {
  formatCurrency as formatDisplayCurrency,
} from "../../shared/utils/displayUtils.js";
import { appData } from "../../app/state/state.js";

const NEUTRAL_FOCUS_BORDER = "#e5e7eb";
const NEUTRAL_FOCUS_SHADOW = "0 0 0 1.5px rgba(71, 85, 105, 0.55)";
const INLINE_FOCUS_LINE = "2px solid rgba(71, 85, 105, 0.55)";
const REQUIRED_ERROR_SHADOW = "0 0 0 1.5px rgba(220, 38, 38, 0.22)";
const TOGGLE_OFF_BACKGROUND = "#cccccc";
const TOGGLE_OFF_HOVER_BACKGROUND = "#b3b3b3";

const FALLBACK_APP_DATA = Object.freeze({
  config: {
    taskStatus: [
      {id: "todo", name: "To do", color: "#9ca3af", order: 1},
      {id: "in_progress", name: "In progress", color: "#60a5fa", order: 2},
      {id: "waiting", name: "Waiting", color: "#f59e0b", order: 3},
      {id: "done", name: "Done", color: "#22c55e", order: 4},
      {id: "needs_review", name: "Needs review", color: "#a78bfa", order: 5},
      {id: "blocked", name: "Blocked", color: "#ef4444", order: 6},
      {id: "scheduled", name: "Scheduled", color: "#38bdf8", order: 7},
      {id: "delegated", name: "Delegated", color: "#f97316", order: 8},
      {id: "awaiting_client", name: "Awaiting client", color: "#fb7185", order: 9},
      {id: "awaiting_lab", name: "Awaiting lab", color: "#c084fc", order: 10},
      {id: "awaiting_supplier", name: "Awaiting supplier", color: "#2dd4bf", order: 11},
      {id: "quality_check", name: "Quality check", color: "#84cc16", order: 12},
      {id: "ready_to_close", name: "Ready to close", color: "#10b981", order: 13},
      {id: "follow_up", name: "Follow-up required", color: "#facc15", order: 14},
      {id: "retired_status", name: "Retired status", color: "#94a3b8", isActive: false, order: 99},
    ],
    priorities: [
      {id: "low", name: "Low", color: "#22c55e", order: 1},
      {id: "normal", name: "Normal", color: "#60a5fa", order: 2},
      {id: "urgent", name: "Urgent", color: "#ef4444", order: 3},
    ],
    taskTypes: [
      {id: "clinical", name: "Clinical follow-up", color: "#14b8a6", order: 1},
      {id: "admin", name: "Admin", color: "#a855f7", order: 2},
      {id: "long_type", name: "Very long task type label for responsive truncation review", color: "#f97316", order: 3},
      {id: "patient_call", name: "Patient call", color: "#60a5fa", order: 4},
      {id: "lab_result", name: "Lab result review", color: "#22c55e", order: 5},
      {id: "stock_check", name: "Stock check", color: "#f59e0b", order: 6},
      {id: "invoice", name: "Invoice follow-up", color: "#ec4899", order: 7},
      {id: "maintenance", name: "Maintenance request", color: "#64748b", order: 8},
      {id: "training", name: "Training", color: "#8b5cf6", order: 9},
      {id: "handover", name: "Team handover", color: "#06b6d4", order: 10},
      {id: "case_review", name: "Case review", color: "#84cc16", order: 11},
      {id: "supplier", name: "Supplier follow-up", color: "#f43f5e", order: 12},
      {id: "reporting", name: "Reporting", color: "#0ea5e9", order: 13},
      {id: "audit", name: "Audit preparation", color: "#d946ef", order: 14},
      {id: "archived_type", name: "Archived legacy type", color: "#64748b", isActive: false, order: 99},
    ],
  },
  users: [
    {
      id: "admin_demo",
      name: "Alex Admin",
      role: "admin",
      permissions: ["patients", "projects", "assets"],
      isActive: true,
      isAppProvider: false,
    },
    {
      id: "provider_demo",
      name: "Priya Provider",
      role: "user",
      permissions: ["patients"],
      isActive: true,
      isAppProvider: true,
    },
    {
      id: "inactive_provider_demo",
      name: "Morgan Historical Provider",
      role: "user",
      permissions: ["patients"],
      isActive: false,
      isAppProvider: true,
    },
    {
      id: "project_user_demo",
      name: "Jordan Projects",
      role: "user",
      permissions: ["projects"],
      isActive: true,
      isAppProvider: false,
    },
  ],
});

const GROUPED_OPTIONS = Object.freeze([
  {value: "today", label: "Due today", group: "Dates"},
  {value: "week", label: "Due this week", group: "Dates"},
  {value: "owner_me", label: "Owned by me", group: "People"},
  {value: "assigned_me", label: "Assigned to me", group: "People"},
  {value: "archived_filter", label: "Legacy saved filter", group: "Archived", archived: true},
]);

const LONG_OPTIONS = Object.freeze([
  {
    value: "short",
    label: "Short label",
  },
  {
    value: "long",
    label: "Exceptionally long label used to verify truncation, wrapping, and mobile-width behaviour without layout shifts",
  },
]);

const TEXT_OPTION_OPTIONS = Object.freeze([
  {value: "Alex Harper", label: "Alex Harper", order: 1},
  {value: "Morgan Lee", label: "Morgan Lee", order: 2},
  {value: "Priya Shah", label: "Priya Shah", order: 3},
  {value: "Samira Chen", label: "Samira Chen", order: 4},
  {value: "Jordan Freelance", label: "Jordan Freelance", order: 5},
  {value: "Taylor Maintenance", label: "Taylor Maintenance", order: 6},
  {value: "Casey Electrical", label: "Casey Electrical", order: 7},
  {value: "Riley Biomedical", label: "Riley Biomedical", order: 8},
  {value: "Noah Contractor", label: "Noah Contractor", order: 9},
]);

const LONG_TEXT_OPTION_OPTIONS = Object.freeze([
  {
    value: "long_technician_label",
    label: "Exceptionally long technician label used to prove truncation and menu width behaviour",
    order: 1,
  },
  {value: "short_label", label: "Short label", order: 2},
]);

const NAVIGATION_ITEMS = Object.freeze([
  {id: "tasks", label: "My Tasks", icon: "checklist"},
  {id: "patients", label: "Patients", icon: "group"},
  {id: "projects", label: "Projects", icon: "work"},
  {id: "assets", label: "Assets", icon: "inventory_2"},
  {id: "drugs", label: "Controlled Drugs", icon: "medication"},
]);

let activeHandles = [];
let nextReviewItemNumber = 1;

function getSandboxData() {
  return FALLBACK_APP_DATA;
}

function getReviewLabel(label) {
  const numberedLabel = `${label} (${nextReviewItemNumber})`;
  nextReviewItemNumber += 1;
  return numberedLabel;
}

function createSection(title, description = "") {
  const section = document.createElement("section");
  section.className = "space-y-4 border-b border-gray-200 pb-6 last:border-b-0 last:pb-0";

  const header = document.createElement("div");
  header.className = "space-y-1";

  const heading = document.createElement("h3");
  heading.className = "text-base font-semibold text-gray-900";
  heading.textContent = title;
  header.append(heading);

  if (description) {
    const copy = document.createElement("p");
    copy.className = "text-sm text-gray-600";
    copy.textContent = description;
    header.append(copy);
  }

  section.append(header);
  return section;
}

function appendReviewLabel(labelElement, label, options = {}) {
  labelElement.textContent = getReviewLabel(label);
  if (!options.required) {return;}

  const requiredMark = document.createElement("span");
  requiredMark.className = "ml-1 font-semibold text-red-600";
  requiredMark.setAttribute("aria-label", "required");
  requiredMark.textContent = "*";
  labelElement.append(requiredMark);
}

function createControlSlot(label, note = "", options = {}) {
  const field = document.createElement("div");
  field.className = "min-w-0 space-y-1 py-2";

  const labelElement = document.createElement("div");
  labelElement.className = "text-base text-gray-500 sm:text-sm";
  appendReviewLabel(labelElement, label, options);
  field.append(labelElement);

  const root = document.createElement("div");
  root.className = "min-w-0";
  field.append(root);

  if (note) {
    const noteElement = document.createElement("p");
    noteElement.className = "text-sm text-gray-500 sm:text-xs";
    noteElement.textContent = note;
    field.append(noteElement);
  }

  applyMobileInputSizing(field);
  return {field, root};
}

function createNativeField(label, content, note = "", options = {}) {
  const field = document.createElement("div");
  field.className = "min-w-0 space-y-1 py-2";

  const labelElement = document.createElement("div");
  labelElement.className = "text-base text-gray-500 sm:text-sm";
  appendReviewLabel(labelElement, label, options);
  field.append(labelElement);

  if (typeof content === "string") {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = content;
    field.append(...Array.from(wrapper.childNodes));
  } else if (content) {
    field.append(content);
  }

  if (note) {
    const noteElement = document.createElement("p");
    noteElement.className = "text-sm text-gray-500 sm:text-xs";
    noteElement.textContent = note;
    field.append(noteElement);
  }

  bindNeutralFocusBorder(field);
  applyMobileInputSizing(field);
  return field;
}

function applyMobileInputSizing(root) {
  root.querySelectorAll("input, select").forEach((input) => {
    if (["checkbox", "radio", "hidden"].includes(input.type)) {return;}
    input.classList.add("!h-10", "sm:!h-[30px]", "!text-base", "sm:!text-sm");
  });
  root.querySelectorAll("textarea").forEach((textarea) => {
    textarea.classList.add("!text-base", "sm:!text-sm");
  });
  root.querySelectorAll("p").forEach((displayField) => {
    if (!displayField.classList.contains("min-h-[30px]")) {return;}
    displayField.classList.add("!min-h-10", "sm:!min-h-[30px]", "!text-base", "sm:!text-sm", "flex", "items-center");
  });
  root.querySelectorAll("button[role='checkbox'], button[role='radio']").forEach((button) => {
    button.classList.add("!h-6", "!w-6", "sm:!h-4", "sm:!w-4");
  });
  root.querySelectorAll("label:has(button[role='checkbox']), label:has(button[role='radio'])").forEach((label) => {
    label.classList.add("!text-base", "sm:!text-sm");
  });
}

function bindNeutralFocusBorder(root) {
  root.querySelectorAll("input, textarea").forEach((input) => {
    if (["checkbox", "radio"].includes(input.type)) {return;}
    if (input.dataset.neutralFocusManaged === "true") {return;}
    input.addEventListener("focus", () => {
      input.style.borderColor = NEUTRAL_FOCUS_BORDER;
    });
    input.addEventListener("blur", () => {
      input.style.borderColor = "";
    });
  });
}

function bindOverflowIndicator(scrollElement, indicator) {
  const update = () => {
    if (!scrollElement || !indicator) {return;}
    const isOverflowing = scrollElement.scrollHeight > scrollElement.clientHeight;
    const isScrolledToBottom = Math.abs(
      scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight,
    ) < 10;

    indicator.classList.toggle("opacity-0", !isOverflowing || isScrolledToBottom);
  };

  scrollElement.addEventListener("scroll", update);
  requestAnimationFrame(update);
}

function createAutoGrowingTextareaExample() {
  const textarea = document.createElement("textarea");
  textarea.rows = 2;
  textarea.className = [
    "min-h-[60px] w-full resize-none overflow-hidden rounded-md border border-gray-200",
    "bg-white px-2 py-1 text-sm text-gray-700",
    "hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  ].join(" ");
  textarea.value = [
    "Auto-growing treatment approval placeholder.",
    "This second line is included so the example visibly expands on load.",
  ].join("\n");

  const resize = () => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  textarea.addEventListener("input", resize);
  requestAnimationFrame(resize);
  return textarea;
}

function createDetailsOverflowExample() {
  const wrapper = document.createElement("div");
  wrapper.className = "relative";
  const textarea = document.createElement("textarea");
  textarea.className = [
    "long-text-display min-h-[128px] max-h-[200px] w-full resize-none overflow-y-auto rounded-md border border-gray-200",
    "bg-white p-2 text-sm text-gray-700 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
    "transition-all duration-200",
  ].join(" ");
  textarea.value = [
    "Patient has several linked follow-up notes and a longer treatment discussion that mirrors the current task details field behaviour.",
    "",
    "The content intentionally exceeds the capped field height so the overflow arrow appears until the display is scrolled near the bottom.",
    "",
    "Use this example to approve the exact edit-mode treatment used by task and parent item detail fields, including the gradient and disappearing arrow.",
    "",
    "Additional line for overflow review.",
    "Additional line for overflow review.",
    "Additional line for overflow review.",
  ].join("\n");

  const indicator = document.createElement("div");
  indicator.className = [
    "overflow-indicator absolute bottom-0 left-0 right-0 flex h-6 items-end justify-center",
    "bg-gradient-to-t from-white/90 to-transparent pb-1 opacity-0 transition-opacity duration-200",
    "pointer-events-none",
  ].join(" ");
  indicator.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800 dark:text-gray-200" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
  `;
  wrapper.append(textarea, indicator);
  bindOverflowIndicator(textarea, indicator);
  return wrapper;
}

function createRequiredInputExample() {
  const wrapper = document.createElement("div");
  wrapper.className = "relative";

  const input = document.createElement("input");
  input.type = "text";
  input.placeholder = "Required field";
  input.dataset.neutralFocusManaged = "true";
  input.dataset.sandboxRequiredInput = "true";
  input.className = [
    "h-[30px] w-full rounded-md border border-gray-200 bg-white px-2 py-1",
    "text-sm text-gray-700 placeholder:text-gray-400",
    "focus:outline-none",
  ].join(" ");

  let isHovered = false;
  const update = () => {
    const validationAttempted = input.dataset.sandboxValidationAttempted === "true";
    const isInvalid = validationAttempted && input.value.trim() === "";
    const isFocused = document.activeElement === input;
    input.classList.toggle("border-red-600", isInvalid);
    input.classList.toggle("border-gray-200", !isInvalid);
    input.style.borderColor = isInvalid ?
      "#dc2626" :
      (isFocused ? NEUTRAL_FOCUS_BORDER : (isHovered ? "var(--dfc-brand-primary)" : ""));
    input.style.boxShadow = isInvalid ? REQUIRED_ERROR_SHADOW : (isFocused ? NEUTRAL_FOCUS_SHADOW : "");
  };

  input.addEventListener("pointerenter", () => {
    isHovered = true;
    update();
  });
  input.addEventListener("pointerleave", () => {
    isHovered = false;
    update();
  });
  input.addEventListener("mouseover", () => {
    isHovered = true;
    update();
  });
  input.addEventListener("mouseleave", () => {
    isHovered = false;
    update();
  });
  input.addEventListener("input", update);
  input.addEventListener("focus", update);
  input.addEventListener("blur", update);
  wrapper.append(input);
  update();
  return wrapper;
}

function createHelperTextExample() {
  const wrapper = document.createElement("div");
  wrapper.className = "space-y-1";
  wrapper.innerHTML = `
    <input type="text" placeholder="Clinical review" class="h-[30px] w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 placeholder:text-gray-400 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]">
    <p class="text-xs text-gray-500">Helper text stays muted and non-blocking.</p>
  `;
  return wrapper;
}

function createCurrencyFieldExample() {
  const input = document.createElement("input");
  input.type = "text";
  input.inputMode = "decimal";
  input.value = "$1,250.00";
  input.placeholder = "$0.00";
  input.className = [
    "h-[30px] w-full rounded-md border border-gray-200 bg-white px-2 py-1",
    "text-sm text-gray-700 placeholder:text-gray-400 hover:border-[var(--dfc-brand-primary)] focus:outline-none",
    "focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  ].join(" ");

  const parseCurrency = () => {
    if (input.value.trim() === "") {return null;}
    const numericValue = Number(input.value.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(numericValue) ? numericValue : null;
  };
  const formatCurrency = () => {
    const value = parseCurrency();
    if (value === null) {
      input.value = "";
      return;
    }
    input.value = new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
    }).format(value);
  };

  input.addEventListener("focus", () => {
    const value = parseCurrency();
    input.value = value === null ? "" : String(value);
    if (input.value) {input.select();}
  });
  restrictCurrencyInput(input);
  input.addEventListener("blur", formatCurrency);
  return input;
}

function createCompactIntegerExample() {
  const input = document.createElement("input");
  input.type = "number";
  input.inputMode = "numeric";
  input.min = "0";
  input.max = "999";
  input.step = "1";
  input.value = "30";
  input.className = [
    "h-[30px] w-14 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm",
    "text-gray-700 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]",
  ].join(" ");
  input.addEventListener("input", () => {
    input.value = input.value.replace(/\D/g, "").slice(0, 3);
  });
  return input;
}

function createInlineEditFieldExample() {
  const wrapper = document.createElement("div");
  wrapper.className = "flex min-w-0 items-center";

  const input = document.createElement("input");
  input.type = "text";
  input.value = "Treatment alerts";
  input.dataset.neutralFocusManaged = "true";
  input.className = [
    "min-w-0 flex-1 rounded-none border-0 border-b border-transparent bg-transparent px-0 py-0.5",
    "text-sm font-semibold text-gray-800 transition-colors",
    "focus:outline-none",
  ].join(" ");
  const applyHoverLine = () => {
    if (document.activeElement !== input) {
      input.style.borderBottom = "1px solid var(--dfc-brand-primary)";
    }
  };
  const clearHoverLine = () => {
    if (document.activeElement !== input) {
      input.style.borderBottom = "";
    }
  };
  input.addEventListener("pointerenter", applyHoverLine);
  input.addEventListener("mouseover", applyHoverLine);
  input.addEventListener("mouseenter", applyHoverLine);
  input.addEventListener("pointerleave", clearHoverLine);
  input.addEventListener("mouseleave", clearHoverLine);
  input.addEventListener("focus", () => {
    input.style.borderBottom = INLINE_FOCUS_LINE;
    input.style.boxShadow = "none";
  });
  input.addEventListener("blur", () => {
    input.style.borderBottom = "";
    input.style.boxShadow = "none";
  });

  wrapper.append(input);
  return wrapper;
}

function createCheckedOffChecklistExample() {
  const wrapper = document.createElement("div");
  let checked = true;
  wrapper.className = "checklist-item-row flex min-h-7 items-start sm:min-h-5";
  wrapper.dataset.id = "sandbox-completed-checklist";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "mr-2 mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors focus:outline-none";
  button.setAttribute("role", "checkbox");

  const icon = document.createElement("span");
  icon.className = "material-symbols-outlined text-[18px] leading-none sm:text-[13px]";
  icon.textContent = "check";

  const text = document.createElement("div");
  text.className = "checklist-item-text-display min-h-7 w-full break-words pr-6 text-base leading-7 sm:min-h-[20px] sm:text-sm sm:leading-5";
  text.textContent = "Confirmed lab result has been reviewed";

  const render = () => {
    button.setAttribute("aria-checked", String(checked));
    button.style.backgroundColor = checked ? "#9ca3af" : "";
    button.style.borderColor = checked ? "#9ca3af" : "#d1d5db";
    button.style.color = checked ? "#ffffff" : "transparent";
    icon.classList.toggle("opacity-0", !checked);
    text.classList.toggle("line-through", checked);
    text.classList.toggle("text-gray-400", checked);
    text.classList.toggle("text-gray-700", !checked);
  };

  const applyHover = () => {
    button.style.borderColor = "var(--dfc-brand-primary)";
  };
  const clearHover = () => render();

  button.append(icon);
  button.addEventListener("click", () => {
    checked = !checked;
    render();
  });
  button.addEventListener("pointerenter", applyHover);
  button.addEventListener("mouseover", applyHover);
  button.addEventListener("mouseenter", applyHover);
  button.addEventListener("pointerleave", clearHover);
  button.addEventListener("mouseleave", clearHover);
  wrapper.append(button, text);
  render();
  return wrapper;
}

function createCheckboxExample() {
  let checked = true;
  const label = document.createElement("label");
  label.className = "inline-flex min-h-7 items-center gap-2 text-base text-gray-700 sm:min-h-5 sm:text-sm";

  const button = document.createElement("button");
  button.type = "button";
  button.className = "inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border border-gray-300 transition-colors focus:outline-none";
  button.setAttribute("role", "checkbox");

  const icon = document.createElement("span");
  icon.className = "material-symbols-outlined text-[18px] leading-none sm:text-[13px]";
  icon.textContent = "check";

  const render = () => {
    button.setAttribute("aria-checked", String(checked));
    button.style.backgroundColor = checked ? "var(--dfc-brand-primary)" : "";
    button.style.borderColor = checked ? "var(--dfc-brand-primary)" : "#d1d5db";
    button.style.color = checked ? "var(--dfc-brand-primary-contrast)" : "transparent";
    icon.classList.toggle("opacity-0", !checked);
  };
  const applyHover = () => {
    button.style.borderColor = "var(--dfc-brand-primary)";
  };
  const clearHover = () => render();

  button.append(icon);
  button.addEventListener("click", () => {
    checked = !checked;
    render();
  });
  button.addEventListener("pointerenter", applyHover);
  button.addEventListener("mouseover", applyHover);
  button.addEventListener("mouseenter", applyHover);
  button.addEventListener("pointerleave", clearHover);
  button.addEventListener("mouseleave", clearHover);
  label.append(button, document.createTextNode("Active"));
  render();
  return label;
}

function createRadioGroupExample() {
  let value = "board";
  const wrapper = document.createElement("div");
  wrapper.className = "flex flex-wrap gap-3 text-base text-gray-700 sm:text-sm";

  const options = [
    {value: "board", label: "Board"},
    {value: "table", label: "Table"},
  ];

  const renderButton = (option) => {
    const label = document.createElement("label");
    label.className = "inline-flex min-h-7 items-center gap-2 sm:min-h-5";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors focus:outline-none";
    button.setAttribute("role", "radio");

    const dot = document.createElement("span");
    dot.className = "h-2 w-2 rounded-full transition-colors";

    const render = () => {
      const selected = value === option.value;
      button.setAttribute("aria-checked", String(selected));
      button.style.backgroundColor = "#ffffff";
      button.style.borderColor = selected ? "#111827" : "#d1d5db";
      dot.style.backgroundColor = selected ? "var(--dfc-brand-primary)" : "transparent";
    };
    const applyHover = () => {
      button.style.borderColor = "var(--dfc-brand-primary)";
    };

    button.append(dot);
    button.addEventListener("click", () => {
      value = option.value;
      wrapper.querySelectorAll("[data-radio-render]").forEach((element) => element.dispatchEvent(new Event("sandbox:radio-render")));
    });
    button.addEventListener("sandbox:radio-render", render);
    button.dataset.radioRender = "true";
    button.addEventListener("pointerenter", applyHover);
    button.addEventListener("mouseover", applyHover);
    button.addEventListener("mouseenter", applyHover);
    button.addEventListener("pointerleave", render);
    button.addEventListener("mouseleave", render);
    label.append(button, document.createTextNode(option.label));
    render();
    return label;
  };

  options.forEach((option) => wrapper.append(renderButton(option)));
  return wrapper;
}

function createToggleExample(options = {}) {
  const {
    checked: initialChecked = true,
    disabled = false,
    invalid = false,
    label = "Toggle setting",
  } = options;
  let checked = initialChecked;

  const control = document.createElement("label");
  control.className = [
    "relative inline-block h-6 w-10 align-middle",
    disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
  ].join(" ");
  control.title = label;

  const input = document.createElement("input");
  input.type = "checkbox";
  input.className = "absolute h-0 w-0 opacity-0";
  input.checked = checked;
  input.disabled = disabled;
  if (invalid) {
    input.dataset.sandboxRequiredToggle = "true";
  }
  input.setAttribute("aria-label", label);
  input.setAttribute("aria-invalid", "false");

  const track = document.createElement("span");
  track.className = "absolute inset-0 rounded-full transition-colors duration-300";

  const knob = document.createElement("span");
  knob.className = "absolute h-4 w-4 rounded-full transition-transform duration-300";
  knob.style.left = "4px";
  knob.style.bottom = "4px";

  const render = () => {
    checked = input.checked;
    const validationAttempted = input.dataset.sandboxValidationAttempted === "true";
    const isRequiredInvalid = invalid && validationAttempted && !checked;
    const isHovered = !disabled && control.matches(":hover");
    track.style.backgroundColor = checked ?
      (isHovered ? "var(--dfc-brand-primary-hover)" : "var(--dfc-brand-primary)") :
      (isHovered ? TOGGLE_OFF_HOVER_BACKGROUND : TOGGLE_OFF_BACKGROUND);
    track.style.border = "0";
    track.style.outline = "";
    track.style.boxShadow = "";
    knob.style.backgroundColor = "#ffffff";
    knob.style.transform = checked ? "translateX(16px)" : "translateX(0)";
    input.setAttribute("aria-invalid", String(isRequiredInvalid));

    if (disabled) {
      track.style.backgroundColor = checked ? "var(--dfc-brand-primary)" : TOGGLE_OFF_BACKGROUND;
      knob.style.backgroundColor = "#e5e7eb";
    }

    if (isRequiredInvalid) {
      track.style.backgroundColor = isHovered ? TOGGLE_OFF_HOVER_BACKGROUND : TOGGLE_OFF_BACKGROUND;
      track.style.outline = "1px solid #dc2626";
      track.style.boxShadow = REQUIRED_ERROR_SHADOW;
    }
  };

  const applyHover = () => {
    if (disabled) {return;}
    render();
  };

  input.addEventListener("change", render);
  control.addEventListener("pointerenter", applyHover);
  control.addEventListener("mouseover", applyHover);
  control.addEventListener("mouseenter", applyHover);
  control.addEventListener("pointerleave", render);
  control.addEventListener("mouseleave", render);
  track.append(knob);
  control.append(input, track);
  render();
  return control;
}

function createToggleSetExample() {
  const wrapper = document.createElement("div");
  wrapper.className = "flex flex-wrap items-center gap-4";
  wrapper.append(
    createToggleExample({checked: true, label: "Standard toggle"}),
    createToggleExample({checked: true, disabled: true, label: "Locked on toggle"}),
    createToggleExample({checked: false, disabled: true, label: "Locked off toggle"}),
    createToggleExample({checked: false, invalid: true, label: "Required toggle"}),
  );
  return wrapper;
}

/**
 * Mirrors save-time validation attempts into native sandbox-only required examples.
 * @param {HTMLFormElement} form Sandbox form.
 * @param {boolean} validationAttempted Whether validation should be visible.
 */
function updateNativeRequiredExamples(form, validationAttempted) {
  const attemptedValue = String(Boolean(validationAttempted));
  form.querySelectorAll("[data-sandbox-required-input], [data-sandbox-required-toggle]")
    .forEach((input) => {
      input.dataset.sandboxValidationAttempted = attemptedValue;
      input.dispatchEvent(new Event(input.type === "checkbox" ? "change" : "input"));
    });
}

function createIconButtonExamples() {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = `
    <div class="flex flex-wrap items-center gap-3">
      <button type="button" class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--dfc-brand-primary)] text-gray-800 shadow-sm transition-colors hover:bg-[var(--dfc-brand-primary-hover)]" title="Add">
        <span class="material-symbols-outlined pointer-events-none text-[22px]">add</span>
      </button>
      <button type="button" class="icon-action-btn table-action-btn" title="Quick close">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
        </svg>
      </button>
    </div>
  `;
  return wrapper.firstElementChild;
}

function createIconNavigationExample() {
  let active = true;
  const button = document.createElement("button");
  button.type = "button";
  button.className = "inline-flex rounded-full p-1 transition-colors focus:outline-none";
  button.title = "Navigation settings";
  button.setAttribute("aria-pressed", String(active));
  button.innerHTML = '<span class="material-symbols-outlined text-2xl">settings</span>';

  const render = () => {
    button.setAttribute("aria-pressed", String(active));
    button.style.backgroundColor = "";
    button.style.color = active ? "var(--dfc-brand-accent)" : "#6b7280";
  };

  button.addEventListener("click", () => {
    active = !active;
    render();
  });
  const applyHover = () => {
    button.style.backgroundColor = "var(--dfc-brand-primary)";
    button.style.color = "var(--dfc-brand-primary-contrast)";
  };
  button.addEventListener("pointerenter", applyHover);
  button.addEventListener("mouseover", applyHover);
  button.addEventListener("mouseenter", applyHover);
  button.addEventListener("pointerleave", render);
  button.addEventListener("mouseleave", render);
  render();
  return button;
}

function createSpecialActionIconExamples() {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = `
    <div class="flex flex-wrap items-center gap-3">
      <button type="button" class="p-1 text-gray-400 transition-colors hover:text-gray-700 focus:outline-none" title="Archive">
        <span class="material-symbols-outlined text-lg">archive</span>
      </button>
      <button type="button" class="p-1 text-gray-400 transition-colors hover:text-red-500 focus:outline-none" title="Delete">
        <span class="material-symbols-outlined text-lg">delete</span>
      </button>
      <button type="button" class="p-1 text-gray-400 transition-colors hover:text-green-600 focus:outline-none" title="Recurring task">
        <span class="material-symbols-outlined text-lg">event_repeat</span>
      </button>
    </div>
  `;
  return wrapper.firstElementChild;
}

function createDesktopNavigationExample() {
  let activeId = "stock";
  const wrapper = document.createElement("div");
  wrapper.className = "space-y-3";
  const subMenuItems = [
    {id: "actions", label: "Action Centre"},
    {id: "stock", label: "Stock"},
    {id: "history", label: "History"},
    {id: "audit", label: "Audit"},
  ];

  const render = () => {
    const activeItem = subMenuItems.find((item) => item.id === activeId) || subMenuItems[0];
    wrapper.innerHTML = `
      <div class="flex border-b border-gray-200">
          ${subMenuItems.map((item) => {
            const isActive = item.id === activeId;
            const activeStyle = isActive ?
              'style="border-color: var(--dfc-brand-accent); color: var(--dfc-brand-accent);"' :
              "";
            const inactiveClasses = isActive ?
              "" :
              "border-transparent text-gray-700 hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)]";
            return `
              <button
                type="button"
                class="sandbox-desktop-nav-tab border-b-2 px-4 py-2 transition-colors duration-200 focus:outline-none ${isActive ? "font-semibold" : ""} ${inactiveClasses}"
                data-nav-target="${item.id}"
                ${activeStyle}
              >${item.label}</button>
            `;
          }).join("")}
      </div>
      <div class="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        ${activeItem.label} selected
      </div>
    `;
  };

  wrapper.addEventListener("click", (event) => {
    const tab = event.target.closest(".sandbox-desktop-nav-tab");
    if (!tab) {return;}
    activeId = tab.dataset.navTarget || activeId;
    render();
  });

  render();
  return wrapper;
}

function createMobileNavigationExample() {
  let activeId = "drugs";
  let activeDrugSubId = "stock";
  let isOpen = true;
  const wrapper = document.createElement("div");
  wrapper.className = "max-w-sm space-y-3";
  const drugSubItems = [
    {id: "actions", label: "Action Centre"},
    {id: "stock", label: "Stock"},
    {id: "history", label: "History"},
    {id: "audit", label: "Audit"},
  ];

  const render = () => {
    const activeItem = NAVIGATION_ITEMS.find((item) => item.id === activeId) || NAVIGATION_ITEMS[0];
    const activeDrugSubItem = drugSubItems.find((item) => item.id === activeDrugSubId) || drugSubItems[0];
    wrapper.innerHTML = `
      <div class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div class="flex items-center justify-between border-b border-gray-200 px-3 py-2">
          <div class="min-w-0">
            <p class="truncate text-sm font-semibold text-gray-900">${activeItem.label}</p>
          </div>
          <button
            type="button"
            class="sandbox-mobile-menu-toggle inline-flex rounded-full p-1 text-gray-500 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
            aria-expanded="${String(isOpen)}"
            aria-label="${isOpen ? "Close navigation menu" : "Open navigation menu"}"
          >
            <span class="material-symbols-outlined text-[20px]">${isOpen ? "close" : "menu"}</span>
          </button>
        </div>
        <div class="${isOpen ? "" : "hidden"} bg-white">
          ${NAVIGATION_ITEMS.map((item) => {
            const isActive = item.id === activeId;
            const activeStyle = isActive ? 'style="color: var(--dfc-brand-accent);"' : "";
            const inactiveClasses = isActive ? "" : "text-gray-700 hover:bg-gray-50 hover:text-gray-900";
            const subMenu = item.id === "drugs" && isActive ? `
              <div class="border-y border-gray-100 bg-gray-50/70 py-1 pl-10 pr-2">
                ${drugSubItems.map((subItem) => {
                  const isSubActive = subItem.id === activeDrugSubId;
                  const subActiveStyle = isSubActive ? 'style="color: var(--dfc-brand-accent);"' : "";
                  const subInactiveClasses = isSubActive ? "" : "text-gray-500 hover:text-[var(--dfc-brand-primary)]";
                  return `
                    <button
                      type="button"
                      class="sandbox-mobile-subnav-option block w-full rounded px-2 py-1.5 text-left text-sm font-medium transition-colors ${subInactiveClasses}"
                      data-subnav-target="${subItem.id}"
                      ${subActiveStyle}
                    >${subItem.label}</button>
                  `;
                }).join("")}
              </div>
            ` : "";
            return `
              <button
                type="button"
                class="sandbox-mobile-nav-option flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium transition-colors ${inactiveClasses}"
                data-nav-target="${item.id}"
                ${activeStyle}
              >
                <span class="material-symbols-outlined text-[20px]">${item.icon}</span>
                <span class="min-w-0 flex-1 truncate">${item.label}</span>
              </button>
              ${subMenu}
            `;
          }).join("")}
          <div class="flex justify-end gap-2 border-t border-gray-100 px-3 py-2">
            <button type="button" class="inline-flex rounded-full p-1 text-gray-500 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none" title="Settings">
              <span class="material-symbols-outlined text-[20px]">settings</span>
            </button>
            <button type="button" class="inline-flex rounded-full p-1 text-gray-500 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none" title="Sign out">
              <span class="material-symbols-outlined text-[20px]">logout</span>
            </button>
          </div>
        </div>
      </div>
      <div class="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
        ${activeItem.id === "drugs" ? `${activeItem.label} / ${activeDrugSubItem.label}` : activeItem.label} selected
      </div>
    `;
  };

  wrapper.addEventListener("click", (event) => {
    if (event.target.closest(".sandbox-mobile-menu-toggle")) {
      isOpen = !isOpen;
      render();
      return;
    }

    const option = event.target.closest(".sandbox-mobile-nav-option");
    const subOption = event.target.closest(".sandbox-mobile-subnav-option");
    if (subOption) {
      activeDrugSubId = subOption.dataset.subnavTarget || activeDrugSubId;
      isOpen = false;
      render();
      return;
    }
    if (!option) {return;}
    activeId = option.dataset.navTarget || activeId;
    if (activeId !== "drugs") {
      isOpen = false;
    }
    render();
  });

  render();
  return wrapper;
}

function createFixedTableExample() {
  const wrapper = document.createElement("div");
  wrapper.className = "overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm";
  const stockColumns = [
    {key: "drug", label: "Drug", align: "left", width: 32},
    {key: "fridge", label: "Fridge", align: "left", width: 17},
    {key: "safe", label: "Safe", align: "left", width: 17},
    {key: "ward", label: "Ward", align: "left", width: 17},
    {key: "total", label: "Total", align: "left", width: 17},
  ];
  const stockRows = [
    {drug: "Example 10mg", fridge: "4", safe: "7", ward: "1", total: "12"},
    {drug: "Example 20mg", fridge: "2", safe: "5", ward: "0", total: "7"},
  ];
  wrapper.innerHTML = getSandboxTableHtml({
    columns: stockColumns,
    rows: stockRows,
    primaryKey: "drug",
  });
  return wrapper;
}

const SANDBOX_TABLE_COLUMNS = Object.freeze([
  {key: "date", label: "Date", width: 15, align: "left", filter: {kind: "dateRange"}},
  {key: "action", label: "Action", width: 24, align: "left", filter: {kind: "checkboxList"}},
  {key: "drug", label: "Drug", width: 23, align: "left", filter: {kind: "checkboxList"}},
  {key: "location", label: "Location", width: 24, align: "left", filter: {kind: "checkboxList"}},
  {key: "quantity", label: "Qty", width: 14, align: "left", filter: {kind: "none", plannedKind: "numberRange"}},
]);

const SANDBOX_TABLE_ROWS = Object.freeze([
  {date: "1 May", dateValue: "2026-05-01", action: "Restock", drug: "Example 10mg", location: "Fridge", quantity: "+4"},
  {date: "3 May", dateValue: "2026-05-03", action: "Use", drug: "Example 20mg", location: "Ward", quantity: "-1"},
  {date: "8 May", dateValue: "2026-05-08", action: "Correction", drug: "Example 10mg", location: "Safe", quantity: "+2"},
  {date: "10 May", dateValue: "2026-05-10", action: "Audit", drug: "Example 50mg", location: "Treatment", quantity: "0"},
  {date: "12 May", dateValue: "2026-05-12", action: "Transfer", drug: "Example 5mg", location: "Theatre", quantity: "-2"},
  {date: "14 May", dateValue: "2026-05-14", action: "Return", drug: "Example 30mg", location: "Dispensary", quantity: "+1"},
  {date: "16 May", dateValue: "2026-05-16", action: "Count", drug: "Example 40mg", location: "Cupboard", quantity: "0"},
  {date: "18 May", dateValue: "2026-05-18", action: "Waste", drug: "Example 15mg", location: "Surgery", quantity: "-1"},
  {date: "20 May", dateValue: "2026-05-20", action: "Receive", drug: "Example 25mg", location: "Pharmacy", quantity: "+6"},
  {date: "22 May", dateValue: "2026-05-22", action: "Reconcile", drug: "Example 35mg", location: "Ward", quantity: "0"},
  {date: "24 May", dateValue: "2026-05-24", action: "Dispose", drug: "Example 45mg", location: "Safe", quantity: "-3"},
  {date: "26 May", dateValue: "2026-05-26", action: "Correction", drug: "Example 60mg", location: "Fridge", quantity: "+2"},
]);

const SANDBOX_FILTER_KIND_COLUMNS = Object.freeze([
  {key: "record", label: "Record", width: 24, align: "left", filter: {kind: "keyword"}},
  {key: "quantity", label: "Qty Change", width: 18, align: "left", filter: {kind: "numberRange", valueKey: "quantityValue"}},
  {key: "value", label: "Value", width: 18, align: "left", filter: {kind: "numberRange", valueKey: "valueAmount", format: "currency"}},
  {key: "note", label: "Note", width: 26, align: "left", filter: {kind: "keyword"}},
  {key: "reconciled", label: "Reconciled", width: 14, align: "left", filter: {kind: "boolean", valueKey: "reconciledValue"}},
]);

const SANDBOX_FILTER_KIND_ROWS = Object.freeze([
  {record: "Adjustment A", quantity: "+12", quantityValue: 12, value: "$148.50", valueAmount: 148.5, note: "Supplier credit applied", reconciled: "Yes", reconciledValue: true},
  {record: "Adjustment B", quantity: "-4", quantityValue: -4, value: "-$32.75", valueAmount: -32.75, note: "Breakage correction", reconciled: "No", reconciledValue: false},
  {record: "Adjustment C", quantity: "+2", quantityValue: 2, value: "$19.20", valueAmount: 19.2, note: "Manual count update", reconciled: "Yes", reconciledValue: true},
  {record: "Adjustment D", quantity: "-9", quantityValue: -9, value: "-$126.00", valueAmount: -126, note: "Expired stock disposal", reconciled: "No", reconciledValue: false},
  {record: "Adjustment E", quantity: "0", quantityValue: 0, value: "$0.00", valueAmount: 0, note: "Audit note only", reconciled: "Yes", reconciledValue: true},
]);

const BOARD_MENU_FILTERS = Object.freeze([
  {
    key: "owner",
    label: "Owner",
    options: [
      {value: "alex", label: "Alex Admin", count: 8},
      {value: "priya", label: "Priya Provider", count: 5},
      {value: "jordan", label: "Jordan Projects", count: 3},
      {value: "sam", label: "Sam Scheduler", count: 6},
      {value: "morgan", label: "Morgan Manager", count: 4},
      {value: "casey", label: "Casey Coordinator", count: 2},
      {value: "riley", label: "Riley Reception", count: 7},
      {value: "taylor", label: "Taylor Technician", count: 3},
      {value: "jamie", label: "Jamie Junior Vet", count: 5},
      {value: "harper", label: "Harper Hospital Lead", count: 1},
    ],
  },
  {
    key: "status",
    label: "Status",
    options: [
      {value: "todo", label: "To do", count: 4},
      {value: "in_progress", label: "In progress", count: 7},
      {value: "waiting", label: "Waiting", count: 2},
      {value: "review", label: "Needs review", count: 3},
      {value: "blocked", label: "Blocked", count: 1},
      {value: "scheduled", label: "Scheduled", count: 6},
      {value: "delegated", label: "Delegated", count: 2},
      {value: "ready", label: "Ready to close", count: 4},
    ],
  },
  {
    key: "type",
    label: "Type",
    options: [
      {value: "clinical", label: "Clinical follow-up", count: 5},
      {value: "admin", label: "Admin", count: 6},
      {value: "stock", label: "Stock review", count: 2},
      {value: "patient_call", label: "Patient call", count: 3},
      {value: "lab_result", label: "Lab result review", count: 5},
      {value: "maintenance", label: "Maintenance request", count: 2},
      {value: "training", label: "Training", count: 1},
    ],
  },
]);

const BOARD_MENU_SORT_OPTIONS = Object.freeze([
  {key: "due", label: "Due date"},
  {key: "priority", label: "Urgency"},
  {key: "updated", label: "Last updated"},
]);

const CHECKBOX_MULTISELECT_OPTIONS = Object.freeze([
  {value: "ketamine", label: "Ketamine 100mg/ml"},
  {value: "midazolam", label: "Midazolam 5mg/ml"},
  {value: "methadone", label: "Methadone 10mg/ml"},
  {value: "butorphanol", label: "Butorphanol 10mg/ml"},
  {value: "alfaxalone", label: "Alfaxalone 10mg/ml"},
  {value: "propofol", label: "Propofol 10mg/ml"},
  {value: "dexmedetomidine", label: "Dexmedetomidine 0.5mg/ml"},
  {value: "medetomidine", label: "Medetomidine 1mg/ml"},
  {value: "atipamezole", label: "Atipamezole 5mg/ml"},
  {value: "buprenorphine", label: "Buprenorphine 0.3mg/ml"},
  {value: "fentanyl", label: "Fentanyl 50mcg/ml"},
  {value: "diazepam", label: "Diazepam 5mg/ml"},
  {value: "lidocaine", label: "Lidocaine 20mg/ml"},
  {value: "bupivacaine", label: "Bupivacaine 5mg/ml"},
]);

function getSandboxCellClass(column, isPrimary = false) {
  const textAlignment = column.align === "right" ? "text-right" : (column.align === "center" ? "text-center" : "text-left");
  return [
    "min-w-0 px-2 py-2 align-middle sm:px-3 sm:py-3",
    textAlignment,
    isPrimary ? "font-medium text-gray-900" : "text-gray-600",
  ].filter(Boolean).join(" ");
}

function getTableTextAlignment(column) {
  if (column.align === "right") {return "right";}
  if (column.align === "center") {return "center";}
  return "left";
}

function getTableFilterKind(column) {
  return column?.filter?.kind || "none";
}

function isTableColumnFilterable(column) {
  return ["checkboxList", "dateRange", "numberRange", "keyword", "boolean"].includes(getTableFilterKind(column));
}

function getTableColumnFilterOptions(column, rows) {
  if (getTableFilterKind(column) !== "checkboxList") {return [];}
  return [...new Set(rows.map((row) => String(row[column.key])))];
}

function getTableRowFilterValue(row, column) {
  const valueKey = column?.filter?.valueKey;
  if (valueKey && Object.prototype.hasOwnProperty.call(row, valueKey)) {
    return row[valueKey];
  }
  return row[`${column.key}Value`] || row[column.key];
}

function parseTableNumberFilterValue(value) {
  if (value === null || value === undefined || String(value).trim() === "") {return null;}
  const number = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(number) ? number : null;
}

function getTableCurrencyFilterDisplayValue(value) {
  const number = parseTableNumberFilterValue(value);
  return number === null ? "" : formatDisplayCurrency(number);
}

function normalizeTableBooleanValue(value) {
  if (value === true || value === "true" || value === "Yes") {return "true";}
  if (value === false || value === "false" || value === "No") {return "false";}
  return "";
}

function doesTableRowMatchFilter(row, column, selectedValues) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {return true;}
  const filterKind = getTableFilterKind(column);
  if (filterKind === "dateRange") {
    const [fromValue = "", toValue = ""] = selectedValues;
    if (!fromValue && !toValue) {return true;}
    const rowValue = getTableRowFilterValue(row, column);
    return (!fromValue || rowValue >= fromValue) && (!toValue || rowValue <= toValue);
  }
  if (filterKind === "checkboxList") {
    return selectedValues.includes(String(getTableRowFilterValue(row, column)));
  }
  if (filterKind === "numberRange") {
    const [minValue = "", maxValue = ""] = selectedValues;
    if (!minValue && !maxValue) {return true;}
    const rowNumber = Number(getTableRowFilterValue(row, column));
    if (!Number.isFinite(rowNumber)) {return false;}
    const minNumber = parseTableNumberFilterValue(minValue);
    const maxNumber = parseTableNumberFilterValue(maxValue);
    return (minNumber === null || rowNumber >= minNumber) &&
      (maxNumber === null || rowNumber <= maxNumber);
  }
  if (filterKind === "keyword") {
    const query = String(selectedValues[0] || "").trim().toLowerCase();
    if (!query) {return true;}
    return String(getTableRowFilterValue(row, column) || "").toLowerCase().includes(query);
  }
  if (filterKind === "boolean") {
    const expected = normalizeTableBooleanValue(selectedValues[0]);
    if (!expected) {return true;}
    return normalizeTableBooleanValue(getTableRowFilterValue(row, column)) === expected;
  }
  return true;
}

function getSandboxTableRows(rows, columns, state = {}) {
  const filters = state.filters || {};
  let workingRows = rows.filter((row) => {
    return columns.every((column) => {
      return doesTableRowMatchFilter(row, column, filters[column.key]);
    });
  });

  if (state.sortKey) {
    const sortColumn = columns.find((column) => column.key === state.sortKey);
    if (sortColumn) {
      workingRows = [...workingRows].sort((left, right) => {
        const leftValue = String(getTableRowFilterValue(left, sortColumn) || "");
        const rightValue = String(getTableRowFilterValue(right, sortColumn) || "");
        return state.sortDirection === "desc" ?
          rightValue.localeCompare(leftValue, undefined, {numeric: true}) :
          leftValue.localeCompare(rightValue, undefined, {numeric: true});
      });
    }
  }

  return workingRows;
}

function getTableSortIconHtml(isSorted, sortDirection) {
  if (!isSorted) {return "";}
  const icon = sortDirection === "asc" ? "expand_less" : "expand_more";
  return `<span class="material-symbols-outlined ml-1 text-[16px] leading-none text-gray-500" aria-hidden="true">${icon}</span>`;
}

function isTableFilterActive(column, values = []) {
  if (!Array.isArray(values) || values.length === 0) {return false;}
  const filterKind = getTableFilterKind(column);
  if (filterKind === "dateRange" || filterKind === "numberRange") {
    return Boolean(values[0] || values[1]);
  }
  if (filterKind === "keyword" || filterKind === "boolean") {
    return Boolean(String(values[0] || "").trim());
  }
  if (filterKind === "checkboxList") {
    return values.length > 0;
  }
  return false;
}

function getTableHeaderControlHtml(column, state = {}) {
  const headerLabel = String(column.label || "").toUpperCase();
  if (!state.withControls) {
    return `<div class="flex h-7 min-w-0 items-center"><span class="block truncate">${headerLabel}</span></div>`;
  }
  const isSorted = state.sortKey === column.key;
  const isFiltered = isTableFilterActive(column, state.filters?.[column.key]);
  const isFilterOpen = state.openFilterKey === column.key;
  const filterActive = isFiltered || isFilterOpen;
  const filterButtonClasses = isFilterOpen ? "bg-gray-100" : "";
  const filterIconClasses = filterActive ?
    "text-[var(--dfc-brand-accent)]" :
    "text-gray-400 group-hover:text-[var(--dfc-brand-primary)] group-focus-visible:text-[var(--dfc-brand-primary)]";
  const filterButtonHtml = isTableColumnFilterable(column) ? `
      <button
        type="button"
        class="filter-btn sandbox-table-filter group inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-200 focus:outline-none focus-visible:bg-gray-100 ${filterButtonClasses}"
        data-filter-key="${column.key}"
        aria-expanded="${String(isFilterOpen)}"
        title="Filter ${column.label}"
      >
        <span class="material-symbols-outlined text-[18px] leading-none ${filterIconClasses}" aria-hidden="true">filter_alt</span>
      </button>
    ` : "";
  return `
    <div class="flex min-w-0 items-center justify-between gap-1">
      <button
        type="button"
        class="sort-icon flex h-7 min-w-0 items-center truncate rounded-md px-1 text-left hover:text-gray-900 focus:outline-none focus-visible:bg-gray-100 focus-visible:text-gray-900"
        data-key="${column.key}"
      ><span class="truncate">${headerLabel}</span>${getTableSortIconHtml(isSorted, state.sortDirection)}</button>
      ${filterButtonHtml}
    </div>
  `;
}

function getTableMobileControlsHtml(columns, state = {}) {
  void columns;
  void state;
  return "";
}

function getSandboxCollisionBoundary(container) {
  const boundary = container.closest("[data-sandbox-collision-boundary]");
  if (boundary) {
    return boundary.getBoundingClientRect();
  }
  return {
    left: 0,
    top: 0,
    right: window.innerWidth || document.documentElement.clientWidth,
    bottom: window.innerHeight || document.documentElement.clientHeight,
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
  };
}

function clampNumber(value, min, max) {
  if (max < min) {return min;}
  return Math.max(min, Math.min(value, max));
}

function positionSandboxFloatingPanel({
  container,
  anchor,
  panel,
  preferredWidth = 288,
  preferredLeft = null,
  gap = 6,
  padding = 8,
}) {
  if (!container || !anchor || !panel) {return;}

  const containerRect = container.getBoundingClientRect();
  const boundaryRect = getSandboxCollisionBoundary(container);
  const anchorRect = anchor.getBoundingClientRect();
  const boundaryWidth = Math.max(0, boundaryRect.right - boundaryRect.left);
  const boundaryHeight = Math.max(0, boundaryRect.bottom - boundaryRect.top);
  const width = Math.min(preferredWidth, Math.max(220, boundaryWidth - padding * 2));
  panel.style.width = `${width}px`;
  panel.style.maxWidth = `${Math.max(0, boundaryWidth - padding * 2)}px`;

  const naturalHeight = Math.max(panel.scrollHeight, panel.getBoundingClientRect().height, 1);
  const maxPanelHeight = Math.max(120, boundaryHeight - padding * 2);
  const height = Math.min(naturalHeight, maxPanelHeight);
  const spaceBelow = boundaryRect.bottom - anchorRect.bottom - gap - padding;
  const spaceAbove = anchorRect.top - boundaryRect.top - gap - padding;
  const placeAbove = spaceBelow < Math.min(height, 180) && spaceAbove > spaceBelow;
  const availableHeight = Math.max(120, placeAbove ? spaceAbove : spaceBelow);
  const resolvedHeight = Math.min(height, availableHeight);
  const minLeft = boundaryRect.left - containerRect.left + padding;
  const maxLeft = boundaryRect.right - containerRect.left - width - padding;
  const targetLeft = Number.isFinite(preferredLeft) ? preferredLeft : anchorRect.left - containerRect.left;
  const left = clampNumber(targetLeft, minLeft, maxLeft);
  const targetTop = placeAbove ?
    anchorRect.top - containerRect.top - gap - resolvedHeight :
    anchorRect.bottom - containerRect.top + gap;
  const minTop = boundaryRect.top - containerRect.top + padding;
  const maxTop = boundaryRect.bottom - containerRect.top - resolvedHeight - padding;
  const top = clampNumber(targetTop, minTop, maxTop);

  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;
  panel.style.marginTop = "0";
  panel.style.maxHeight = `${resolvedHeight}px`;
  panel.style.overflowY = "auto";
  panel.dataset.collisionY = placeAbove ? "up" : "down";
  panel.dataset.collisionX = left < targetLeft ? "left" : (left > targetLeft ? "right" : "aligned");
}

function getTableFilterMenuStyle(state = {}) {
  const position = state.menuPosition || {};
  const left = Number.isFinite(position.left) ? position.left : 12;
  const top = Number.isFinite(position.top) ? position.top : 44;
  const width = Number.isFinite(position.width) ? position.width : 288;
  return `left: ${left}px; top: ${top}px; width: ${width}px;`;
}

function getTableCheckboxMarkerStyle(checked) {
  return `background-color: ${checked ? "var(--dfc-brand-accent)" : "#ffffff"}; border-color: ${checked ? "var(--dfc-brand-accent)" : "#d1d5db"}; color: ${checked ? "#ffffff" : "transparent"};`;
}

function updateTableCheckboxMarker(marker, checked) {
  if (!marker) {return;}
  marker.style.backgroundColor = checked ? "var(--dfc-brand-accent)" : "#ffffff";
  marker.style.borderColor = checked ? "var(--dfc-brand-accent)" : "#d1d5db";
  marker.style.color = checked ? "#ffffff" : "transparent";
}

function getTableFilterCheckboxHtml(option, checked) {
  return `
    <input type="checkbox" class="sandbox-table-filter-checkbox sr-only" value="${option}" tabindex="-1" ${checked ? "checked" : ""}>
    <span class="sandbox-table-filter-checkbox-marker inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors" aria-hidden="true" style="${getTableCheckboxMarkerStyle(checked)}">
      <span class="material-symbols-outlined text-[13px] leading-none">check</span>
    </span>
  `;
}

function getTableFilterBulkLabel(selectedCount) {
  return selectedCount === 0 ? "Select all" : "Deselect all";
}

function getTableFilterBulkIcon(selectedCount) {
  return selectedCount === 0 ? "done_all" : "remove_done";
}

function getTableFilterBulkButtonHtml(columnKey, selectedCount) {
  const label = getTableFilterBulkLabel(selectedCount);
  return `
    <button
      type="button"
      class="sandbox-table-filter-bulk inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:border-[var(--dfc-brand-primary)] focus-visible:text-[var(--dfc-brand-primary)]"
      data-table-filter-bulk="${columnKey}"
      title="${label}"
      aria-label="${label}"
    >
      <span class="material-symbols-outlined text-[18px]">${getTableFilterBulkIcon(selectedCount)}</span>
    </button>
  `;
}

function updateTableFilterBulkButton(button, selectedCount) {
  if (!button) {return;}
  const label = getTableFilterBulkLabel(selectedCount);
  button.title = label;
  button.setAttribute("aria-label", label);
  const icon = button.querySelector(".material-symbols-outlined");
  if (icon) {
    icon.textContent = getTableFilterBulkIcon(selectedCount);
  }
}

function getTableDateFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || ["", ""];
  const [fromValue = "", toValue = ""] = draftValues;
  return `
    <div class="sandbox-table-filter-options space-y-2">
      <label class="block text-xs font-medium text-gray-500">
        From
        <input type="date" class="sandbox-table-filter-date mt-1 h-8 w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" data-date-boundary="from" value="${fromValue}" style="color: ${fromValue ? "#374151" : "#9ca3af"};">
      </label>
      <label class="block text-xs font-medium text-gray-500">
        To
        <input type="date" class="sandbox-table-filter-date mt-1 h-8 w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" data-date-boundary="to" value="${toValue}" style="color: ${toValue ? "#374151" : "#9ca3af"};">
      </label>
    </div>
  `;
}

function getTableNumberRangeFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || ["", ""];
  const [minValue = "", maxValue = ""] = draftValues;
  const isCurrency = column.filter?.format === "currency";
  const inputType = isCurrency ? "text" : "number";
  const inputMode = isCurrency ? 'inputmode="decimal"' : "";
  const step = isCurrency ? "" : 'step="any"';
  const inputValue = (value) => isCurrency ? getTableCurrencyFilterDisplayValue(value) : value;
  const placeholder = isCurrency ? 'placeholder="$0.00"' : "";
  const getInputHtml = (boundary, label, value) => `
    <label class="block text-xs font-medium text-gray-500">
      ${label}
      <span class="relative mt-1 block">
        <input
          type="${inputType}"
          ${step}
          ${inputMode}
          class="sandbox-table-filter-number h-8 w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
          data-number-boundary="${boundary}"
          data-number-format="${isCurrency ? "currency" : "number"}"
          value="${inputValue(value)}"
          ${placeholder}
        >
      </span>
    </label>
  `;
  return `
    <div class="sandbox-table-filter-options space-y-2">
      ${getInputHtml("min", "Min", minValue)}
      ${getInputHtml("max", "Max", maxValue)}
    </div>
  `;
}

function getTableKeywordFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || [""];
  const [query = ""] = draftValues;
  const hasQuery = Boolean(query);
  return `
    <div class="sandbox-table-filter-options" data-table-filter-search-row>
      <span class="relative block w-full">
        <input
          type="search"
          class="menu-search-input sandbox-table-filter-keyword h-8 w-full rounded border border-gray-200 py-1.5 pl-2 pr-8 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
          value="${query}"
          placeholder="Search keyword"
        >
        <button
          type="button"
          class="sandbox-table-filter-search-clear absolute right-1 top-1/2 ${hasQuery ? "inline-flex" : "hidden"} h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]"
          data-table-filter-search-clear
          ${hasQuery ? "" : "disabled"}
          aria-hidden="${String(!hasQuery)}"
          aria-label="Clear search"
        >
          <span class="material-symbols-outlined text-[18px] leading-none">close</span>
        </button>
      </span>
    </div>
  `;
}

function getTableBooleanRadioStyle(selected) {
  return `background-color: #ffffff; border-color: ${selected ? "var(--dfc-brand-accent)" : "#d1d5db"};`;
}

function updateTableBooleanFilterRadios(wrapper, columnKey, selectedValue) {
  wrapper.querySelectorAll(`[data-table-filter-boolean="${columnKey}"]`).forEach((button) => {
    const selected = button.dataset.booleanValue === selectedValue;
    button.setAttribute("aria-checked", String(selected));
    button.style.backgroundColor = "#ffffff";
    button.style.borderColor = selected ? "var(--dfc-brand-accent)" : "#d1d5db";
    const dot = button.querySelector(".sandbox-table-filter-boolean-dot");
    if (dot) {
      dot.style.backgroundColor = selected ? "var(--dfc-brand-accent)" : "transparent";
    }
  });
  wrapper.querySelectorAll(`[data-table-filter-boolean-option="${columnKey}"]`).forEach((choice) => {
    choice.setAttribute("aria-checked", String(choice.dataset.booleanValue === selectedValue));
  });
}

function getTableBooleanFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || [""];
  const [selectedValue = ""] = draftValues;
  const options = [
    {value: "", label: "Any"},
    {value: "true", label: "Yes"},
    {value: "false", label: "No"},
  ];
  return `
    <div class="sandbox-table-filter-options space-y-1">
      ${options.map((option) => {
        const selected = selectedValue === option.value;
        return `
        <label
          class="sandbox-table-filter-choice group -mx-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
          data-table-filter-boolean-option="${column.key}"
          data-boolean-value="${option.value}"
          role="radio"
          aria-checked="${String(selected)}"
          tabindex="-1"
        >
          <button
            type="button"
            class="sandbox-table-filter-boolean inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors group-hover:border-[var(--dfc-brand-accent)] focus:outline-none"
            role="radio"
            aria-checked="${String(selected)}"
            data-table-filter-boolean="${column.key}"
            data-boolean-value="${option.value}"
            tabindex="-1"
            style="${getTableBooleanRadioStyle(selected)}"
          >
            <span class="sandbox-table-filter-boolean-dot h-2 w-2 rounded-full transition-colors" style="background-color: ${selected ? "var(--dfc-brand-accent)" : "transparent"};"></span>
          </button>
          <span>${option.label}</span>
        </label>
      `;
      }).join("")}
    </div>
  `;
}

function getTableListFilterMenuHtml(column, rows, state = {}) {
  const options = getTableColumnFilterOptions(column, rows);
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || options;
  const selectedValues = new Set(draftValues.map(String));
  return `
    <div class="mb-2 flex items-center gap-1.5" data-table-filter-search-row>
      <span class="relative min-w-0 flex-1">
        <input type="search" class="menu-search-input sandbox-table-filter-search h-8 w-full rounded border border-gray-200 py-1.5 pl-2 pr-8 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" placeholder="Search ${column.label}">
        <button
          type="button"
          class="sandbox-table-filter-search-clear absolute right-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]"
          data-table-filter-search-clear
          disabled
          aria-hidden="true"
          aria-label="Clear search"
        >
          <span class="material-symbols-outlined text-[18px] leading-none">close</span>
        </button>
      </span>
      ${getTableFilterBulkButtonHtml(column.key, selectedValues.size)}
    </div>
    <div class="sandbox-table-filter-options space-y-1">
      ${options.map((option) => `
        <label
          class="sandbox-table-filter-choice sandbox-table-filter-option -mx-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
          data-filter-option-label="${option.toLowerCase()}"
          role="checkbox"
          aria-checked="${String(selectedValues.has(option))}"
          tabindex="-1"
        >
          ${getTableFilterCheckboxHtml(option, selectedValues.has(option))}
          <span>${option}</span>
        </label>
      `).join("")}
    </div>
  `;
}

function getTableFilterMenuHtml(filterKey, columns, rows, state = {}) {
  if (!filterKey) {return "";}
  const column = columns.find((entry) => entry.key === filterKey) || columns[0];
  if (!isTableColumnFilterable(column)) {return "";}
  const filterKind = getTableFilterKind(column);
  const bodyHtml = {
    boolean: getTableBooleanFilterMenuHtml,
    dateRange: getTableDateFilterMenuHtml,
    keyword: getTableKeywordFilterMenuHtml,
    numberRange: getTableNumberRangeFilterMenuHtml,
  }[filterKind]?.(column, state) || getTableListFilterMenuHtml(column, rows, state);
  return `
    <div class="sandbox-table-filter-menu absolute z-30 max-w-[calc(100%-24px)] rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-xl" data-filter-menu-key="${column.key}" style="${getTableFilterMenuStyle(state)}">
      <div class="mb-2 flex items-center justify-between gap-3">
        <p class="min-w-0 truncate text-sm font-semibold text-gray-800">Filter ${column.label}</p>
        <button type="button" class="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:bg-gray-100 focus-visible:text-gray-700" data-table-filter-close title="Close filter">
          <span class="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>
      ${bodyHtml}
      <div class="mt-3 flex gap-2 border-t border-gray-100 pt-2">
        <button type="button" class="flex-1 rounded-md border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50" data-table-filter-clear="${column.key}">Clear</button>
        <button type="button" class="flex-1 rounded-md bg-[var(--dfc-brand-primary)] px-3 py-1 text-sm font-semibold text-gray-900 hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus-visible:bg-[var(--dfc-brand-primary-hover)]" data-table-filter-apply="${column.key}">Apply</button>
      </div>
    </div>
  `;
}

function getSandboxMobileRowsHtml(columns, rows, primaryKey) {
  void columns;
  void rows;
  void primaryKey;
  return "";
}

function getSandboxTableHtml(config) {
  const {
    columns,
    rows,
    primaryKey = columns[0]?.key,
    withControls = false,
    adjustable = false,
    state = {},
  } = config;
  const renderedRows = getSandboxTableRows(rows, columns, state);
  return `
    <div class="overflow-hidden rounded-lg">
      ${getTableMobileControlsHtml(columns, {...state, withControls, adjustable})}
      ${getSandboxMobileRowsHtml(columns, renderedRows, primaryKey)}
      <div class="overflow-x-auto">
        <table class="w-full min-w-[560px] table-fixed divide-y divide-gray-200 text-xs sm:min-w-0 sm:text-sm">
          <thead class="border-y border-slate-100 bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              ${columns.map((column) => `
                <th scope="col" class="sortable relative h-11 bg-gray-50 px-2 py-2 text-${getTableTextAlignment(column)} font-medium tracking-wider sm:h-12 sm:px-3" data-key="${column.key}" ${adjustable ? 'draggable="true"' : ""} style="width: ${column.width}%;">
                  ${getTableHeaderControlHtml(column, {...state, withControls})}
                  ${adjustable ? `<div class="table-resizer sandbox-table-resizer hidden sm:block" data-key="${column.key}"></div>` : ""}
                </th>
              `).join("")}
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${renderedRows.length ? renderedRows.map((row) => `
              <tr class="hover:bg-gray-50">
                ${columns.map((column) => `
                  <td class="${getSandboxCellClass(column, column.key === primaryKey)}">
                    <div class="min-w-0 truncate">${row[column.key]}</div>
                  </td>
                `).join("")}
              </tr>
            `).join("") : `
              <tr>
                <td class="px-3 py-4 text-sm text-gray-500" colspan="${columns.length}">No rows match the current filters.</td>
              </tr>
            `}
          </tbody>
        </table>
      </div>
    </div>
    ${state.openFilterKey ? getTableFilterMenuHtml(state.openFilterKey, columns, rows, state) : ""}
  `;
}

function updateTableFilterMenuPosition(wrapper, filterButton, tableState) {
  const wrapperRect = wrapper.getBoundingClientRect();
  const buttonRect = filterButton.getBoundingClientRect();
  const menuWidth = wrapperRect.width < 640 ? Math.max(240, wrapperRect.width - 24) : 288;
  const preferredLeft = buttonRect.left - wrapperRect.left - 12;
  const maxLeft = Math.max(12, wrapperRect.width - menuWidth - 12);
  tableState.menuPosition = {
    left: Math.max(12, Math.min(preferredLeft, maxLeft)),
    top: buttonRect.bottom - wrapperRect.top + 4,
    width: menuWidth,
  };
}

function positionOpenTableFilterMenu(wrapper, tableState) {
  if (!tableState.openFilterKey) {return;}
  const filterButton = wrapper.querySelector(`.sandbox-table-filter[data-filter-key="${tableState.openFilterKey}"]`);
  const panel = wrapper.querySelector(".sandbox-table-filter-menu");
  if (!filterButton || !panel) {return;}
  const wrapperRect = wrapper.getBoundingClientRect();
  const buttonRect = filterButton.getBoundingClientRect();
  const verticalBoundary = getSandboxCollisionBoundary(wrapper);
  const gap = 4;
  const padding = 8;
  const menuWidth = wrapperRect.width < 640 ?
    Math.max(240, Math.min(288, wrapperRect.width - 24)) :
    288;
  const preferredLeft = buttonRect.left - wrapperRect.left - 12;
  const maxLeft = Math.max(padding, wrapperRect.width - menuWidth - padding);
  const left = clampNumber(preferredLeft, padding, maxLeft);

  panel.style.width = `${menuWidth}px`;
  panel.style.maxWidth = `${Math.max(0, wrapperRect.width - padding * 2)}px`;

  const naturalHeight = Math.max(panel.scrollHeight, panel.getBoundingClientRect().height, 1);
  const maxPanelHeight = Math.min(288, Math.max(120, verticalBoundary.height - padding * 2));
  const height = Math.min(naturalHeight, maxPanelHeight);
  const spaceBelow = verticalBoundary.bottom - buttonRect.bottom - gap - padding;
  const spaceAbove = buttonRect.top - verticalBoundary.top - gap - padding;
  const placeAbove = spaceBelow < Math.min(height, 180) && spaceAbove > spaceBelow;
  const availableHeight = Math.max(120, placeAbove ? spaceAbove : spaceBelow);
  const resolvedHeight = Math.min(height, availableHeight);
  const getTargetTop = (panelHeight) => {
    const preferredTop = placeAbove ?
      buttonRect.top - wrapperRect.top - gap - panelHeight :
      buttonRect.bottom - wrapperRect.top + gap;
    const minTop = verticalBoundary.top - wrapperRect.top + padding;
    const maxTop = verticalBoundary.bottom - wrapperRect.top - panelHeight - padding;
    return clampNumber(preferredTop, minTop, maxTop);
  };

  panel.style.left = `${left}px`;
  panel.style.maxHeight = `${resolvedHeight}px`;
  panel.style.overflowY = "auto";
  panel.style.top = `${getTargetTop(Math.min(panel.scrollHeight, resolvedHeight))}px`;
  panel.dataset.collisionX = left < preferredLeft ? "left" : (left > preferredLeft ? "right" : "aligned");
  panel.dataset.collisionY = placeAbove ? "up" : "down";
}

function getVisibleTableFilterChoices(wrapper) {
  const panel = wrapper.querySelector(".sandbox-table-filter-menu");
  if (!panel) {return [];}
  return Array.from(panel.querySelectorAll(".sandbox-table-filter-choice"))
    .filter((choice) => !choice.classList.contains("hidden"));
}

function focusInitialTableFilterTarget(wrapper) {
  const choice = getVisibleTableFilterChoices(wrapper)[0];
  const input = wrapper.querySelector(
    ".sandbox-table-filter-menu .sandbox-table-filter-date, " +
    ".sandbox-table-filter-menu .sandbox-table-filter-number, " +
    ".sandbox-table-filter-menu .sandbox-table-filter-keyword",
  );
  const fallback = wrapper.querySelector(".sandbox-table-filter-menu [data-table-filter-clear]");
  if (choice) {
    choice.tabIndex = 0;
  }
  (choice || input || fallback)?.focus();
}

function focusTableFilterChoice(wrapper, currentChoice, direction) {
  const choices = getVisibleTableFilterChoices(wrapper);
  if (choices.length === 0) {return;}
  const currentIndex = choices.indexOf(currentChoice);
  const nextIndex = currentIndex < 0 ?
    (direction > 0 ? 0 : choices.length - 1) :
    (currentIndex + direction + choices.length) % choices.length;
  choices.forEach((choice) => {
    choice.tabIndex = -1;
  });
  if (choices[nextIndex]) {
    choices[nextIndex].tabIndex = 0;
    choices[nextIndex].focus();
  }
}

function updateTableFilterChoiceChecked(choice, checked) {
  if (!choice) {return;}
  choice.setAttribute("aria-checked", String(checked));
}

function updateSandboxTableFilterClearButton(input) {
  const clearButton = input
    .closest("[data-table-filter-search-row]")
    ?.querySelector("[data-table-filter-search-clear]");
  if (!clearButton) {return;}
  const hasValue = Boolean(input.value);
  clearButton.classList.toggle("hidden", !hasValue);
  clearButton.disabled = !hasValue;
  clearButton.setAttribute("aria-hidden", String(!hasValue));
}

function updateSandboxTableFilterSearch(search, wrapper) {
  if (!search) {return;}
  const query = search.value.trim().toLowerCase();
  wrapper.querySelectorAll(".sandbox-table-filter-option").forEach((option) => {
    option.classList.toggle("hidden", query && !option.dataset.filterOptionLabel.includes(query));
  });
  updateSandboxTableFilterClearButton(search);
}

function updateSandboxTableKeywordFilterInput(keywordInput, tableState) {
  if (!keywordInput || !tableState.openFilterKey) {return;}
  tableState.draftFilters[tableState.openFilterKey] = [keywordInput.value];
  updateSandboxTableFilterClearButton(keywordInput);
}

function getDefaultTableDraftFilterValues(column, rows) {
  const filterKind = getTableFilterKind(column);
  if (filterKind === "dateRange" || filterKind === "numberRange") {return ["", ""];}
  if (filterKind === "keyword" || filterKind === "boolean") {return [""];}
  if (filterKind === "checkboxList") {
    return getTableColumnFilterOptions(column, rows);
  }
  return [];
}

function bindSandboxTableControls(wrapper, tableState, render, rows, getColumns, options = {}) {
  wrapper.addEventListener("click", (event) => {
    const closeButton = event.target.closest("[data-table-filter-close]");
    if (closeButton) {
      tableState.openFilterKey = "";
      render();
      return;
    }

    const searchClearButton = event.target.closest("[data-table-filter-search-clear]");
    if (searchClearButton) {
      const search = searchClearButton
        .closest("[data-table-filter-search-row]")
        ?.querySelector(".sandbox-table-filter-search, .sandbox-table-filter-keyword");
      if (search) {
        search.value = "";
        if (search.matches(".sandbox-table-filter-keyword")) {
          updateSandboxTableKeywordFilterInput(search, tableState);
        } else {
          updateSandboxTableFilterSearch(search, wrapper);
        }
        search.focus();
      }
      return;
    }

    const bulkButton = event.target.closest("[data-table-filter-bulk]");
    if (bulkButton) {
      const key = bulkButton.dataset.tableFilterBulk;
      const column = getColumns().find((entry) => entry.key === key);
      const allOptions = getTableColumnFilterOptions(column, rows);
      const currentValues = tableState.draftFilters[key] || [];
      const nextValues = currentValues.length === 0 ? allOptions : [];
      tableState.draftFilters[key] = nextValues;
      wrapper.querySelectorAll(".sandbox-table-filter-checkbox").forEach((checkbox) => {
        checkbox.checked = nextValues.includes(checkbox.value);
        updateTableFilterChoiceChecked(checkbox.closest(".sandbox-table-filter-choice"), checkbox.checked);
        updateTableCheckboxMarker(
          checkbox.closest(".sandbox-table-filter-option")?.querySelector(".sandbox-table-filter-checkbox-marker"),
          checkbox.checked,
        );
      });
      updateTableFilterBulkButton(bulkButton, nextValues.length);
      return;
    }

    const booleanOption = event.target.closest("[data-table-filter-boolean-option]");
    if (booleanOption && tableState.openFilterKey) {
      const key = tableState.openFilterKey;
      const selectedValue = booleanOption.dataset.booleanValue || "";
      tableState.draftFilters[key] = [selectedValue];
      updateTableBooleanFilterRadios(wrapper, key, selectedValue);
      return;
    }

    const clearButton = event.target.closest("[data-table-filter-clear]");
    if (clearButton) {
      const key = clearButton.dataset.tableFilterClear;
      delete tableState.filters[key];
      delete tableState.draftFilters[key];
      tableState.openFilterKey = "";
      render();
      return;
    }

    const applyButton = event.target.closest("[data-table-filter-apply]");
    if (applyButton) {
      const key = applyButton.dataset.tableFilterApply;
      const columns = getColumns();
      const column = columns.find((entry) => entry.key === key);
      const draftValues = tableState.draftFilters[key] || [];
      const allOptions = getTableColumnFilterOptions(column, rows);
      const filterKind = getTableFilterKind(column);
      const isEmptyDateRange = filterKind === "dateRange" && !draftValues[0] && !draftValues[1];
      const isEmptyNumberRange = filterKind === "numberRange" && !draftValues[0] && !draftValues[1];
      const isEmptyKeyword = filterKind === "keyword" && !String(draftValues[0] || "").trim();
      const isEmptyBoolean = filterKind === "boolean" && !draftValues[0];
      const isEmptyCheckboxList = filterKind === "checkboxList" &&
        (draftValues.length === 0 || draftValues.length === allOptions.length);
      if (
        isEmptyDateRange ||
        isEmptyNumberRange ||
        isEmptyKeyword ||
        isEmptyBoolean ||
        isEmptyCheckboxList ||
        filterKind === "none"
      ) {
        delete tableState.filters[key];
      } else {
        tableState.filters[key] = draftValues;
      }
      tableState.openFilterKey = "";
      render();
      return;
    }

    const filterButton = event.target.closest(".sandbox-table-filter");
    if (filterButton) {
      const key = filterButton.dataset.filterKey;
      const column = getColumns().find((entry) => entry.key === key);
      if (!isTableColumnFilterable(column)) {return;}
      tableState.openFilterKey = tableState.openFilterKey === key ? "" : key;
      if (tableState.openFilterKey) {
        updateTableFilterMenuPosition(wrapper, filterButton, tableState);
        tableState.draftFilters[key] = tableState.filters[key] ?
          [...tableState.filters[key]] :
          getDefaultTableDraftFilterValues(column, rows);
      }
      render();
      if (tableState.openFilterKey) {
        focusInitialTableFilterTarget(wrapper);
      }
      return;
    }

    const sortLabel = event.target.closest(".sort-icon");
    if (sortLabel) {
      const key = sortLabel.dataset.key;
      tableState.sortDirection = tableState.sortKey === key && tableState.sortDirection === "asc" ? "desc" : "asc";
      tableState.sortKey = key;
      render();
      return;
    }

    if (!event.target.closest(".sandbox-table-filter-menu") && tableState.openFilterKey) {
      tableState.openFilterKey = "";
      render();
    }
  });

  wrapper.addEventListener("change", (event) => {
    const checkbox = event.target.closest(".sandbox-table-filter-checkbox");
    if (!checkbox || !tableState.openFilterKey) {return;}
    const key = tableState.openFilterKey;
    const currentValues = new Set(tableState.draftFilters[key] || []);
    if (checkbox.checked) {
      currentValues.add(checkbox.value);
    } else {
      currentValues.delete(checkbox.value);
    }
    tableState.draftFilters[key] = Array.from(currentValues);
    updateTableCheckboxMarker(
      checkbox.closest(".sandbox-table-filter-option")?.querySelector(".sandbox-table-filter-checkbox-marker"),
      checkbox.checked,
    );
    updateTableFilterChoiceChecked(checkbox.closest(".sandbox-table-filter-choice"), checkbox.checked);
    updateTableFilterBulkButton(
      wrapper.querySelector(`[data-table-filter-bulk="${key}"]`),
      tableState.draftFilters[key].length,
    );
  });

  wrapper.addEventListener("beforeinput", (event) => {
    const numberInput = event.target.closest(".sandbox-table-filter-number");
    if (!numberInput || numberInput.dataset.numberFormat !== "currency") {return;}
    if (!event.data || event.inputType?.startsWith("delete") || event.inputType?.startsWith("history")) {
      return;
    }
    if (event.inputType === "insertFromPaste") {
      return;
    }
    if (!CURRENCY_INPUT_ALLOWED_PATTERN.test(event.data)) {
      event.preventDefault();
    }
  });

  wrapper.addEventListener("input", (event) => {
    const dateInput = event.target.closest(".sandbox-table-filter-date");
    if (!dateInput || !tableState.openFilterKey) {return;}
    const key = tableState.openFilterKey;
    const [fromValue = "", toValue = ""] = tableState.draftFilters[key] || ["", ""];
    tableState.draftFilters[key] = dateInput.dataset.dateBoundary === "from" ?
      [dateInput.value, toValue] :
      [fromValue, dateInput.value];
    dateInput.style.color = dateInput.value ? "#374151" : "#9ca3af";
  });

  wrapper.addEventListener("input", (event) => {
    const numberInput = event.target.closest(".sandbox-table-filter-number");
    if (numberInput && tableState.openFilterKey) {
      const originalValue = numberInput.value;
      const nextInputValue = numberInput.dataset.numberFormat === "currency" ?
        sanitizeCurrencyInputValue(originalValue) :
        originalValue;
      if (nextInputValue !== originalValue) {
        const selectionStart = numberInput.selectionStart ?? originalValue.length;
        const cleanedBeforeCursor = sanitizeCurrencyInputValue(originalValue.slice(0, selectionStart));
        numberInput.value = nextInputValue;
        numberInput.setSelectionRange?.(cleanedBeforeCursor.length, cleanedBeforeCursor.length);
      }
      const key = tableState.openFilterKey;
      const [minValue = "", maxValue = ""] = tableState.draftFilters[key] || ["", ""];
      tableState.draftFilters[key] = numberInput.dataset.numberBoundary === "min" ?
        [nextInputValue, maxValue] :
        [minValue, nextInputValue];
      return;
    }

    const keywordInput = event.target.closest(".sandbox-table-filter-keyword");
    if (keywordInput && tableState.openFilterKey) {
      updateSandboxTableKeywordFilterInput(keywordInput, tableState);
      return;
    }

    const search = event.target.closest(".sandbox-table-filter-search");
    if (!search) {return;}
    updateSandboxTableFilterSearch(search, wrapper);
  });

  wrapper.addEventListener("focusin", (event) => {
    const numberInput = event.target.closest(".sandbox-table-filter-number");
    if (!numberInput || numberInput.dataset.numberFormat !== "currency") {return;}
    const value = parseTableNumberFilterValue(numberInput.value);
    numberInput.value = value === null ? "" : String(value);
    if (numberInput.value) {numberInput.select();}
  });

  wrapper.addEventListener("focusout", (event) => {
    const numberInput = event.target.closest(".sandbox-table-filter-number");
    if (!numberInput || numberInput.dataset.numberFormat !== "currency" || !tableState.openFilterKey) {return;}
    const key = tableState.openFilterKey;
    const [minValue = "", maxValue = ""] = tableState.draftFilters[key] || ["", ""];
    const parsedValue = parseTableNumberFilterValue(numberInput.value);
    const nextValue = parsedValue === null ? "" : String(parsedValue);
    tableState.draftFilters[key] = numberInput.dataset.numberBoundary === "min" ?
      [nextValue, maxValue] :
      [minValue, nextValue];
    numberInput.value = parsedValue === null ? "" : formatDisplayCurrency(parsedValue);
  });

  wrapper.addEventListener("keydown", (event) => {
    if (!tableState.openFilterKey) {return;}

    if (event.key === "Escape") {
      const key = tableState.openFilterKey;
      event.preventDefault();
      tableState.openFilterKey = "";
      render();
      wrapper.querySelector(`.sandbox-table-filter[data-filter-key="${key}"]`)?.focus();
      return;
    }

    const choice = event.target.closest(".sandbox-table-filter-choice");
    const isSearch = Boolean(event.target.closest(".sandbox-table-filter-search"));
    const isMenuAction = Boolean(event.target.closest(
      ".sandbox-table-filter-bulk, " +
      "[data-table-filter-search-clear], " +
      "[data-table-filter-close], " +
      "[data-table-filter-clear], " +
      "[data-table-filter-apply]",
    ));
    if (!choice && !isSearch && !isMenuAction) {return;}

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      focusTableFilterChoice(wrapper, choice, event.key === "ArrowDown" ? 1 : -1);
      return;
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const choices = getVisibleTableFilterChoices(wrapper);
      const target = event.key === "Home" ? choices[0] : choices[choices.length - 1];
      target?.focus();
      return;
    }

    if (!choice || (event.key !== "Enter" && event.key !== " ")) {return;}
    event.preventDefault();
    const checkbox = choice.querySelector(".sandbox-table-filter-checkbox");
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change", {bubbles: true}));
      return;
    }
    if (choice.matches("[data-table-filter-boolean-option]")) {
      choice.click();
    }
  });

  if (options.enableResize) {
    bindSandboxTableResize(wrapper, tableState, render, getColumns);
  }
}

function createFixedTableWithControlsExample(options = {}) {
  const wrapper = document.createElement("div");
  wrapper.className = "relative rounded-lg border border-gray-200 bg-white shadow-sm";
  const tableState = {
    sortKey: "date",
    sortDirection: "asc",
    filters: {location: ["Fridge", "Safe"]},
    draftFilters: {},
    openFilterKey: options.initialOpenFilterKey || "",
  };
  const columns = SANDBOX_TABLE_COLUMNS.map((column) => {
    if (options.compactFirstColumn && column.key === "date") {
      return {...column, width: 8};
    }
    if (options.compactFirstColumn && column.key === "action") {
      return {...column, width: 31};
    }
    return {...column};
  });
  if (tableState.openFilterKey) {
    const column = columns.find((entry) => entry.key === tableState.openFilterKey);
    tableState.draftFilters[tableState.openFilterKey] = tableState.filters[tableState.openFilterKey] ?
      [...tableState.filters[tableState.openFilterKey]] :
      getDefaultTableDraftFilterValues(column, SANDBOX_TABLE_ROWS);
  }
  const render = () => {
    wrapper.innerHTML = getSandboxTableHtml({
      columns,
      rows: SANDBOX_TABLE_ROWS,
      primaryKey: "date",
      withControls: true,
      state: tableState,
    });
    positionOpenTableFilterMenu(wrapper, tableState);
    requestAnimationFrame(() => positionOpenTableFilterMenu(wrapper, tableState));
  };

  bindSandboxTableControls(wrapper, tableState, render, SANDBOX_TABLE_ROWS, () => columns);
  render();
  return wrapper;
}

function createAdjustableTableExample(options = {}) {
  const {withControls = false} = options;
  const wrapper = document.createElement("div");
  wrapper.className = "relative rounded-lg border border-gray-200 bg-white shadow-sm";
  let columns = SANDBOX_TABLE_COLUMNS.map((column) => ({...column}));
  const tableState = {
    draggedKey: "",
    sortKey: withControls ? "date" : "",
    sortDirection: "asc",
    filters: withControls ? {drug: ["Example 10mg"]} : {},
    draftFilters: {},
    openFilterKey: options.initialOpenFilterKey || "",
  };
  if (tableState.openFilterKey) {
    const column = columns.find((entry) => entry.key === tableState.openFilterKey);
    tableState.draftFilters[tableState.openFilterKey] = tableState.filters[tableState.openFilterKey] ?
      [...tableState.filters[tableState.openFilterKey]] :
      getDefaultTableDraftFilterValues(column, SANDBOX_TABLE_ROWS);
  }

  const render = () => {
    wrapper.innerHTML = getSandboxTableHtml({
      columns,
      rows: SANDBOX_TABLE_ROWS,
      primaryKey: "date",
      withControls,
      adjustable: true,
      state: tableState,
    });
    positionOpenTableFilterMenu(wrapper, tableState);
    requestAnimationFrame(() => positionOpenTableFilterMenu(wrapper, tableState));
  };

  bindSandboxTableControls(wrapper, tableState, render, SANDBOX_TABLE_ROWS, () => columns, {enableResize: true});

  wrapper.addEventListener("dragstart", (event) => {
    const header = event.target.closest(".sortable");
    if (!header || event.target.closest(".sandbox-table-resizer, button")) {return;}
    tableState.draggedKey = header.dataset.key || "";
    header.classList.add("dragging-header");
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", tableState.draggedKey);
  });

  wrapper.addEventListener("dragover", (event) => {
    if (!tableState.draggedKey) {return;}
    const targetHeader = event.target.closest(".sortable");
    if (!targetHeader || targetHeader.dataset.key === tableState.draggedKey) {return;}
    event.preventDefault();
    wrapper.querySelectorAll(".drag-over-header").forEach((element) => {
      if (element !== targetHeader) {element.classList.remove("drag-over-header");}
    });
    targetHeader.classList.add("drag-over-header");
    event.dataTransfer.dropEffect = "move";
  });

  wrapper.addEventListener("drop", (event) => {
    const header = event.target.closest(".sortable");
    if (!header || !tableState.draggedKey || header.dataset.key === tableState.draggedKey) {return;}
    event.preventDefault();
    header.classList.remove("drag-over-header");
    const fromIndex = columns.findIndex((column) => column.key === tableState.draggedKey);
    const toIndex = columns.findIndex((column) => column.key === header.dataset.key);
    if (fromIndex < 0 || toIndex < 0) {return;}
    const nextColumns = [...columns];
    const [movedColumn] = nextColumns.splice(fromIndex, 1);
    nextColumns.splice(toIndex, 0, movedColumn);
    columns = nextColumns;
    tableState.draggedKey = "";
    render();
  });

  wrapper.addEventListener("dragend", () => {
    tableState.draggedKey = "";
    wrapper.querySelectorAll(".dragging-header, .drag-over-header").forEach((element) => {
      element.classList.remove("dragging-header", "drag-over-header");
    });
  });

  render();
  return wrapper;
}

function createFilterKindTableExample(options = {}) {
  const wrapper = document.createElement("div");
  wrapper.className = "relative rounded-lg border border-gray-200 bg-white shadow-sm";
  let columns = SANDBOX_FILTER_KIND_COLUMNS.map((column) => ({...column}));
  const tableState = {
    draggedKey: "",
    sortKey: "record",
    sortDirection: "asc",
    filters: {},
    draftFilters: {},
    openFilterKey: options.initialOpenFilterKey || "",
  };
  if (tableState.openFilterKey) {
    const column = columns.find((entry) => entry.key === tableState.openFilterKey);
    tableState.draftFilters[tableState.openFilterKey] = tableState.filters[tableState.openFilterKey] ?
      [...tableState.filters[tableState.openFilterKey]] :
      getDefaultTableDraftFilterValues(column, SANDBOX_FILTER_KIND_ROWS);
  }

  const render = () => {
    const renderedRows = getSandboxTableRows(SANDBOX_FILTER_KIND_ROWS, columns, tableState);
    wrapper.innerHTML = `
      <div class="overflow-hidden rounded-lg">
        <div class="overflow-x-auto">
          <table class="w-full min-w-[560px] table-fixed divide-y divide-gray-200 text-xs sm:min-w-0 sm:text-sm">
            <thead class="border-y border-slate-100 bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                ${columns.map((column) => `
                  <th scope="col" class="sortable relative h-11 bg-gray-50 px-2 py-2 text-${getTableTextAlignment(column)} font-medium tracking-wider sm:h-12 sm:px-3" data-key="${column.key}" draggable="true" style="width: ${column.width}%;">
                    ${getTableHeaderControlHtml(column, {...tableState, withControls: true})}
                    <div class="table-resizer sandbox-table-resizer hidden sm:block" data-key="${column.key}"></div>
                  </th>
                `).join("")}
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              ${renderedRows.length ? renderedRows.map((row) => `
                <tr class="hover:bg-gray-50">
                  ${columns.map((column) => `
                    <td class="${getSandboxCellClass(column, column.key === "record")}">
                      <div class="min-w-0 truncate">${row[column.key]}</div>
                    </td>
                  `).join("")}
                </tr>
              `).join("") : `
                <tr>
                  <td class="px-3 py-4 text-sm text-gray-500" colspan="${columns.length}">No rows match the current filters.</td>
                </tr>
              `}
            </tbody>
          </table>
        </div>
      </div>
      ${tableState.openFilterKey ? getTableFilterMenuHtml(tableState.openFilterKey, columns, SANDBOX_FILTER_KIND_ROWS, tableState) : ""}
    `;
    positionOpenTableFilterMenu(wrapper, tableState);
    requestAnimationFrame(() => positionOpenTableFilterMenu(wrapper, tableState));
  };

  bindSandboxTableControls(wrapper, tableState, render, SANDBOX_FILTER_KIND_ROWS, () => columns);
  bindSandboxTableResize(wrapper, tableState, render, () => columns);

  wrapper.addEventListener("dragstart", (event) => {
    const header = event.target.closest(".sortable");
    if (!header || event.target.closest(".sandbox-table-resizer, button")) {return;}
    tableState.draggedKey = header.dataset.key || "";
    header.classList.add("dragging-header");
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", tableState.draggedKey);
  });

  wrapper.addEventListener("dragover", (event) => {
    if (!tableState.draggedKey) {return;}
    const targetHeader = event.target.closest(".sortable");
    if (!targetHeader || targetHeader.dataset.key === tableState.draggedKey) {return;}
    event.preventDefault();
    wrapper.querySelectorAll(".drag-over-header").forEach((element) => {
      if (element !== targetHeader) {element.classList.remove("drag-over-header");}
    });
    targetHeader.classList.add("drag-over-header");
    event.dataTransfer.dropEffect = "move";
  });

  wrapper.addEventListener("drop", (event) => {
    const header = event.target.closest(".sortable");
    if (!header || !tableState.draggedKey || header.dataset.key === tableState.draggedKey) {return;}
    event.preventDefault();
    header.classList.remove("drag-over-header");
    const fromIndex = columns.findIndex((column) => column.key === tableState.draggedKey);
    const toIndex = columns.findIndex((column) => column.key === header.dataset.key);
    if (fromIndex < 0 || toIndex < 0) {return;}
    const nextColumns = [...columns];
    const [movedColumn] = nextColumns.splice(fromIndex, 1);
    nextColumns.splice(toIndex, 0, movedColumn);
    columns = nextColumns;
    tableState.draggedKey = "";
    render();
  });

  wrapper.addEventListener("dragend", () => {
    tableState.draggedKey = "";
    wrapper.querySelectorAll(".dragging-header, .drag-over-header").forEach((element) => {
      element.classList.remove("dragging-header", "drag-over-header");
    });
  });

  render();
  return wrapper;
}

function bindSandboxTableResize(wrapper, tableState, render, getColumns) {
  const startResize = (event, header) => {
    event.preventDefault();
    if (!header) {return;}
    const startX = event.clientX;
    const key = header.dataset.key;
    const columns = getColumns();
    const columnIndex = columns.findIndex((column) => column.key === key);
    const neighborIndex = columnIndex === columns.length - 1 ? columnIndex - 1 : columnIndex + 1;
    if (columnIndex < 0 || neighborIndex < 0) {return;}
    const startWidth = columns[columnIndex].width;
    const neighborStartWidth = columns[neighborIndex].width;
    const wrapperWidth = wrapper.getBoundingClientRect().width || 1;
    const direction = neighborIndex > columnIndex ? 1 : -1;

    const onMouseMove = (moveEvent) => {
      const deltaPercent = ((moveEvent.clientX - startX) / wrapperWidth) * 100 * direction;
      const nextWidth = Math.max(10, Math.min(startWidth + deltaPercent, startWidth + neighborStartWidth - 10));
      const nextNeighborWidth = startWidth + neighborStartWidth - nextWidth;
      columns[columnIndex] = {...columns[columnIndex], width: Number(nextWidth.toFixed(2))};
      columns[neighborIndex] = {...columns[neighborIndex], width: Number(nextNeighborWidth.toFixed(2))};
      wrapper.querySelectorAll("th[data-key]").forEach((cell) => {
        const column = columns.find((entry) => entry.key === cell.dataset.key);
        if (column) {cell.style.width = `${column.width}%`;}
      });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      tableState.openFilterKey = "";
      render();
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  wrapper.addEventListener("mousedown", (event) => {
    const resizer = event.target.closest(".sandbox-table-resizer");
    if (resizer) {
      event.stopPropagation();
      startResize(event, resizer.closest("th"));
      return;
    }

    const header = event.target.closest("th[data-key]");
    if (!header) {return;}
    const rect = header.getBoundingClientRect();
    if (rect.right - event.clientX <= 10) {
      startResize(event, header);
    }
  });
}

function getBoardMenuSelectedCount(filters = {}) {
  return Object.values(filters).reduce((total, values) => {
    return total + (Array.isArray(values) ? values.length : 0);
  }, 0);
}

function getMenuCheckboxHtml({
  checked = false,
  className = "",
  dataAttributes = "",
  value = "",
}) {
  return `
    <button
      type="button"
      class="${className} inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors focus:outline-none"
      role="checkbox"
      aria-checked="${String(checked)}"
      data-checkbox-value="${value}"
      tabindex="-1"
      ${dataAttributes}
      style="background-color: ${checked ? "var(--dfc-brand-accent)" : "#ffffff"}; border-color: ${checked ? "var(--dfc-brand-accent)" : "#d1d5db"}; color: ${checked ? "#ffffff" : "transparent"};"
    >
      <span class="material-symbols-outlined text-[13px] leading-none">check</span>
    </button>
  `;
}

function getBoardMenuButtonHtml({menuId, icon, active, badgeCount = 0}) {
  const activeStyle = active ?
    'style="border-color: var(--dfc-brand-accent); color: var(--dfc-brand-accent);"' :
    "";
  const inactiveClasses = active ? "" : "border-gray-200 text-gray-500";
  return `
    <button
      type="button"
      class="sandbox-board-menu-trigger relative inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white shadow-sm transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none ${inactiveClasses}"
      data-board-menu-trigger="${menuId}"
      ${activeStyle}
    >
      <span class="material-symbols-outlined pointer-events-none text-[20px]">${icon}</span>
      ${badgeCount > 0 ? `
        <span class="pointer-events-none absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--dfc-brand-accent)] px-1.5 text-[10px] font-semibold text-white">${badgeCount}</span>
      ` : ""}
    </button>
  `;
}

function getBoardSortMenuHtml(state) {
  const directionGlyph = state.sortDirection === "desc" ? "▼" : "▲";
  return `
    <div class="mb-2 text-sm font-semibold text-gray-500">Sort</div>
    <div class="space-y-1">
      ${BOARD_MENU_SORT_OPTIONS.map((option) => {
        const selected = state.sortKey === option.key;
        const selectedStyle = selected ? 'style="color: var(--dfc-brand-accent);"' : "";
        const inactiveClasses = selected ? "" : "text-gray-700 hover:bg-gray-50";
        return `
          <button
            type="button"
            class="flex w-full items-center rounded-xl px-3 py-2 text-left text-sm transition-colors ${inactiveClasses}"
            data-board-sort-option="${option.key}"
            ${selectedStyle}
          >
            <span class="inline-flex min-w-0 items-center gap-1.5">
              <span class="truncate">${option.label}</span>
              ${selected ? `<span class="text-[11px] leading-none">${directionGlyph}</span>` : ""}
            </span>
          </button>
        `;
      }).join("")}
    </div>
    <button
      type="button"
      class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-accent)] hover:text-[var(--dfc-brand-accent)] focus:outline-none"
      data-board-sort-clear
    >
      Clear Sort
    </button>
  `;
}

function getBoardFilterMenuHtml(state) {
  const activeFilter = BOARD_MENU_FILTERS.find((filter) => filter.key === state.filterPage);
  if (!activeFilter) {
    return `
      <div class="mb-2 text-sm font-semibold text-gray-500">Filter</div>
      <div class="space-y-1">
        ${BOARD_MENU_FILTERS.map((filter) => {
          const selectedCount = state.filters[filter.key]?.length || 0;
          return `
            <button
              type="button"
              class="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
              data-board-filter-category="${filter.key}"
            >
              <span>${filter.label}</span>
              ${selectedCount > 0 ? `
                <span class="text-xs font-semibold text-[var(--dfc-brand-accent)]">${selectedCount}</span>
              ` : ""}
            </button>
          `;
        }).join("")}
      </div>
      <button
        type="button"
        class="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-[var(--dfc-brand-accent)] hover:text-[var(--dfc-brand-accent)] focus:outline-none"
        data-board-filter-clear-all
      >
        Clear Filters
      </button>
    `;
  }

  const selectedValues = new Set(state.filters[activeFilter.key] || []);
  const selectedCount = selectedValues.size;
  return `
    <button
      type="button"
      class="mb-2 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[var(--dfc-brand-accent)] focus:outline-none"
      data-board-filter-back
    >
      <span class="material-symbols-outlined text-[18px]">arrow_back</span>
      <span>Filter</span>
    </button>
    <div class="mb-2 flex items-center justify-between gap-3">
      <div class="min-w-0 text-sm font-semibold text-gray-500">${activeFilter.label}</div>
      ${selectedCount > 0 ? `
        <button
          type="button"
          class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-transparent bg-white text-xs leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
          data-board-filter-clear-category="${activeFilter.key}"
          aria-label="Clear ${activeFilter.label} filter"
        >
          <span class="pointer-events-none">x</span>
        </button>
      ` : ""}
    </div>
    <div class="max-h-64 space-y-1 overflow-y-auto pr-1">
      ${activeFilter.options.map((option) => `
        <label class="sandbox-board-filter-option flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50" data-board-filter-key="${activeFilter.key}" data-checkbox-value="${option.value}">
          ${getMenuCheckboxHtml({
            checked: selectedValues.has(option.value),
            className: "sandbox-board-filter-checkbox",
            dataAttributes: `data-board-filter-key="${activeFilter.key}"`,
            value: option.value,
          })}
          <span class="min-w-0 flex-1 truncate">${option.label}</span>
          <span class="shrink-0 text-xs text-gray-400">(${option.count})</span>
        </label>
      `).join("")}
    </div>
  `;
}

function positionOpenBoardMenuPanel(wrapper, menuId) {
  if (!menuId) {return;}
  const trigger = wrapper.querySelector(`[data-board-menu-trigger="${menuId}"]`);
  const panel = wrapper.querySelector(`[data-board-menu-panel="${menuId}"]`);
  if (!trigger || !panel) {return;}
  const width = menuId === "filter" ? 256 : 240;
  positionSandboxFloatingPanel({
    container: wrapper,
    anchor: trigger,
    panel,
    preferredWidth: width,
    preferredLeft: trigger.getBoundingClientRect().left - wrapper.getBoundingClientRect().left,
    gap: 6,
    padding: 8,
  });
}

function createBoardSortFilterMenuExample(options = {}) {
  const wrapper = document.createElement("div");
  wrapper.className = options.anchor === "bottomRight" ?
    "relative min-h-[210px]" :
    "relative space-y-3";
  const state = {
    openMenu: options.initialOpenMenu || "",
    filterPage: options.initialFilterPage || "",
    sortKey: "due",
    sortDirection: "asc",
    filters: {
      owner: ["alex", "priya"],
      status: ["in_progress"],
    },
  };

  const render = () => {
    const filterCount = getBoardMenuSelectedCount(state.filters);
    const sortActive = Boolean(state.sortKey);
    const rowClass = options.anchor === "bottomRight" ?
      "absolute bottom-3 right-3 flex flex-wrap items-start gap-3" :
      "flex flex-wrap items-start gap-3";
    wrapper.innerHTML = `
      <div class="${rowClass}">
        <div class="relative" data-board-menu-wrapper="sort">
          ${getBoardMenuButtonHtml({
            menuId: "sort",
            icon: "swap_vert",
            active: state.openMenu === "sort" || sortActive,
          })}
        </div>
        <div class="relative" data-board-menu-wrapper="filter">
          ${getBoardMenuButtonHtml({
            menuId: "filter",
            icon: "filter_alt",
            active: state.openMenu === "filter" || filterCount > 0,
            badgeCount: filterCount,
          })}
          ${filterCount > 0 ? `
            <button
              type="button"
              class="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-transparent bg-white text-[11px] leading-none text-gray-400 transition-colors hover:bg-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary-contrast)] focus:outline-none"
              data-board-filter-trigger-clear
              aria-label="Clear filters"
            >
              <span class="pointer-events-none">x</span>
            </button>
          ` : ""}
        </div>
      </div>
      ${state.openMenu === "sort" ? `
        <div class="absolute z-20 rounded-2xl border border-gray-200 bg-white p-3 shadow-xl" data-board-menu-panel="sort">
          ${getBoardSortMenuHtml(state)}
        </div>
      ` : ""}
      ${state.openMenu === "filter" ? `
        <div class="absolute z-20 rounded-2xl border border-gray-200 bg-white p-3 shadow-xl" data-board-menu-panel="filter">
          ${getBoardFilterMenuHtml(state)}
        </div>
      ` : ""}
      <p class="text-xs text-gray-500">
        ${sortActive ? "Due date ascending" : "No sort"} · ${filterCount} filter selections
      </p>
    `;
    positionOpenBoardMenuPanel(wrapper, state.openMenu);
    requestAnimationFrame(() => positionOpenBoardMenuPanel(wrapper, state.openMenu));
  };

  wrapper.addEventListener("click", (event) => {
    event.stopPropagation();
    const trigger = event.target.closest("[data-board-menu-trigger]");
    if (trigger) {
      const menuId = trigger.dataset.boardMenuTrigger;
      state.openMenu = state.openMenu === menuId ? "" : menuId;
      if (menuId === "filter" && !state.openMenu) {state.filterPage = "";}
      render();
      return;
    }

    if (event.target.closest("[data-board-filter-trigger-clear]")) {
      state.filters = {};
      state.filterPage = "";
      render();
      return;
    }

    const sortOption = event.target.closest("[data-board-sort-option]");
    if (sortOption) {
      const key = sortOption.dataset.boardSortOption;
      state.sortDirection = state.sortKey === key && state.sortDirection === "asc" ? "desc" : "asc";
      state.sortKey = key;
      render();
      return;
    }

    if (event.target.closest("[data-board-sort-clear]")) {
      state.sortKey = "";
      state.sortDirection = "asc";
      render();
      return;
    }

    const filterCategory = event.target.closest("[data-board-filter-category]");
    if (filterCategory) {
      state.filterPage = filterCategory.dataset.boardFilterCategory || "";
      render();
      return;
    }

    if (event.target.closest("[data-board-filter-back]")) {
      state.filterPage = "";
      render();
      return;
    }

    const categoryClearButton = event.target.closest("[data-board-filter-clear-category]");
    if (categoryClearButton) {
      delete state.filters[categoryClearButton.dataset.boardFilterClearCategory];
      render();
      return;
    }

    if (event.target.closest("[data-board-filter-clear-all]")) {
      state.filters = {};
      state.filterPage = "";
      render();
      return;
    }

    const checkboxOption = event.target.closest(".sandbox-board-filter-option");
    if (checkboxOption) {
      const filterKey = checkboxOption.dataset.boardFilterKey;
      const value = checkboxOption.dataset.checkboxValue;
      const nextValues = new Set(state.filters[filterKey] || []);
      if (nextValues.has(value)) {
        nextValues.delete(value);
      } else {
        nextValues.add(value);
      }
      state.filters[filterKey] = Array.from(nextValues);
      if (state.filters[filterKey].length === 0) {
        delete state.filters[filterKey];
      }
      render();
    }
  });

  const closeOnOutsideClick = (event) => {
    if (!state.openMenu || wrapper.contains(event.target)) {return;}
    state.openMenu = "";
    state.filterPage = "";
    render();
  };
  document.addEventListener("click", closeOnOutsideClick);
  activeHandles.push({destroy: () => document.removeEventListener("click", closeOnOutsideClick)});

  render();
  return wrapper;
}

function createCollisionProofViewport(content, label, options = {}) {
  const frame = document.createElement("div");
  frame.className = "space-y-2";

  const caption = document.createElement("p");
  caption.className = "text-xs font-medium text-gray-500";
  caption.textContent = label;
  frame.append(caption);

  const viewport = document.createElement("div");
  viewport.className = "relative overflow-hidden rounded-lg border border-dashed border-gray-300 bg-gray-50";
  viewport.dataset.sandboxCollisionBoundary = "true";
  viewport.style.height = options.height || "240px";
  viewport.style.width = options.width || "100%";

  const holder = document.createElement("div");
  holder.className = "absolute";
  holder.style.width = options.contentWidth || "560px";
  if (options.placement === "topLeft") {
    holder.style.left = options.left || "-24px";
    holder.style.top = "12px";
  } else {
    holder.style.right = "12px";
    holder.style.bottom = options.bottom || "12px";
  }
  holder.append(content);
  viewport.append(holder);
  frame.append(viewport);
  return frame;
}

function mountBoardMenuGallery(form) {
  const section = createSection("Board Filter Menus");
  const grid = createGrid();
  const boardMenuStack = document.createElement("div");
  boardMenuStack.className = "space-y-4";
  boardMenuStack.append(
    createBoardSortFilterMenuExample(),
    createCollisionProofViewport(
      createBoardSortFilterMenuExample({
        anchor: "bottomRight",
        initialOpenMenu: "filter",
        initialFilterPage: "owner",
      }),
      "Restricted bottom/right viewport replica",
      {height: "230px", width: "360px", contentWidth: "340px", placement: "bottomRight"},
    ),
  );
  const boardMenus = createNativeField(
    "Board sort/filter menus",
    boardMenuStack,
    "Filter categories open a second page, keep internal option scroll, and avoid constrained viewport edges.",
  );
  boardMenus.classList.add("lg:col-span-2");
  grid.append(boardMenus);
  section.append(grid);
  form.append(section);
}

function createGrid() {
  const grid = document.createElement("div");
  grid.className = "grid grid-cols-1 gap-x-4 gap-y-5 lg:grid-cols-2";
  return grid;
}

function addHandle(handle) {
  if (handle) {
    activeHandles.push(handle);
  }
}

function formatJson(value) {
  return JSON.stringify(value, null, 2);
}

function updateSnapshot(form, options = {}) {
  const shouldValidate = Boolean(options.validate);
  updateNativeRequiredExamples(form, shouldValidate);

  const extraction = getRegisteredControlValues(form, {validate: shouldValidate});
  const allValues = getRegisteredControlValues(form, {includeDisabled: true});
  const summary = shouldValidate ?
    validateRegisteredControls(form) :
    {valid: true, errors: []};
  const output = document.getElementById("sandbox-values-output");
  if (!output) {return;}

  output.textContent = formatJson({
    valid: summary.valid && extraction.valid,
    values: extraction.values,
    disabledIncludedValues: allValues.values,
    errors: extraction.errors,
    sources: extraction.sources,
  });
}

function bindSnapshotUpdates(form) {
  const hasValidationAttempt = () => form.dataset.sandboxValidationAttempted === "true";
  form.addEventListener("control:valuechange", () => updateSnapshot(form, {validate: hasValidationAttempt()}));
  form.addEventListener("change", () => updateSnapshot(form, {validate: hasValidationAttempt()}));

  const validateButton = document.getElementById("sandbox-validate-btn");
  validateButton?.addEventListener("click", () => {
    form.dataset.sandboxValidationAttempted = "true";
    updateSnapshot(form, {validate: true});
  });
}

function mountSelectGallery(form, data) {
  const section = createSection(
    "Select Controls",
    "Single, searchable, grouped, archived, disabled, and long-label states.",
  );
  const grid = createGrid();

  const status = createControlSlot("Status select", "Config-backed colour accents.");
  addHandle(createStatusSelectPreset({
    root: status.root,
    appData: data,
    itemType: "task",
    value: data.config.taskStatus?.[1]?.id || "",
    searchable: true,
  }));
  grid.append(status.field);

  const priority = createControlSlot("Urgency select", "Dynamic priority options.");
  addHandle(createPrioritySelectPreset({
    root: priority.root,
    appData: data,
    value: data.config.priorities?.[1]?.id || "",
  }));
  grid.append(priority.field);

  const grouped = createControlSlot("Grouped searchable select", "Includes an archived saved option.");
  addHandle(createSelectControl({
    root: grouped.root,
    name: "sandbox_grouped_filter",
    value: "archived_filter",
    options: GROUPED_OPTIONS,
    searchable: true,
    clearable: true,
    placeholder: "+ Add filter",
  }));
  grid.append(grouped.field);

  const required = createControlSlot(
    "Required select",
    "Standard required state shows only the label asterisk until validation.",
    {required: true},
  );
  addHandle(createSelectControl({
    root: required.root,
    name: "sandbox_required_owner",
    value: "",
    options: GROUPED_OPTIONS.filter((option) => !option.archived),
    required: true,
    searchable: true,
    placeholder: "+ Add owner",
  }));
  grid.append(required.field);

  const disabled = createControlSlot("Disabled select", "Included only in disabled-inclusive extraction.");
  addHandle(createSelectControl({
    root: disabled.root,
    name: "sandbox_disabled_select",
    value: "today",
    options: GROUPED_OPTIONS,
    disabled: true,
  }));
  grid.append(disabled.field);

  const longLabel = createControlSlot("Long label select", "Checks truncation in narrow layouts.");
  addHandle(createSelectControl({
    root: longLabel.root,
    name: "sandbox_long_label",
    value: "long",
    options: LONG_OPTIONS,
    clearable: true,
  }));
  grid.append(longLabel.field);

  section.append(grid);
  form.append(section);
}

function mountTextOptionGallery(form) {
  const section = createSection(
    "Selections with free input",
    "Configured-option menus that also accept typed free text.",
  );
  const grid = createGrid();

  const configured = createControlSlot(
    "Empty field with options",
    "Blank starting state; open the menu, search, choose a configured option, or type a new value.",
  );
  addHandle(createTextOptionControl({
    root: configured.root,
    name: "sandbox_text_option_configured",
    value: "",
    options: TEXT_OPTION_OPTIONS,
    placeholder: "Select or enter technician",
    accessibleName: "Empty free-input select with options",
  }));
  grid.append(configured.field);

  const typed = createControlSlot(
    "Saved custom text",
    "Existing value is not in the configured list, so it remains editable text without a selected-menu tick.",
  );
  addHandle(createTextOptionControl({
    root: typed.root,
    name: "sandbox_text_option_typed",
    value: "External contractor",
    options: TEXT_OPTION_OPTIONS,
    placeholder: "Select or enter technician",
    accessibleName: "Saved custom free-input text",
  }));
  grid.append(typed.field);

  const selected = createControlSlot(
    "Saved configured option",
    "Existing value matches the configured list, so its menu row shows accent text and a tick.",
  );
  addHandle(createTextOptionControl({
    root: selected.root,
    name: "sandbox_text_option_selected",
    value: "Priya Shah",
    options: TEXT_OPTION_OPTIONS,
    placeholder: "Select or enter technician",
    accessibleName: "Saved configured free-input option",
  }));
  grid.append(selected.field);

  const required = createControlSlot(
    "Required free-input select",
    "Standard required state shows only the label asterisk until validation.",
    {required: true},
  );
  addHandle(createTextOptionControl({
    root: required.root,
    name: "sandbox_text_option_required",
    value: "",
    options: TEXT_OPTION_OPTIONS,
    placeholder: "Select or enter technician",
    accessibleName: "Required free-input select",
    required: true,
  }));
  grid.append(required.field);

  const longLabel = createControlSlot("Long option label", "Checks truncation in narrow layouts.");
  addHandle(createTextOptionControl({
    root: longLabel.root,
    name: "sandbox_text_option_long_label",
    value: "long_technician_label",
    options: LONG_TEXT_OPTION_OPTIONS,
    placeholder: "Select or enter technician",
    accessibleName: "Long-label free-input select",
  }));
  grid.append(longLabel.field);

  const disabled = createControlSlot("Disabled free-input select", "Included only in disabled-inclusive extraction.");
  addHandle(createTextOptionControl({
    root: disabled.root,
    name: "sandbox_text_option_disabled",
    value: "Morgan Lee",
    options: TEXT_OPTION_OPTIONS,
    placeholder: "Select or enter technician",
    accessibleName: "Disabled free-input select",
    disabled: true,
  }));
  grid.append(disabled.field);

  section.append(grid);
  form.append(section);
}

function mountMultiSelectGallery(form, data) {
  const section = createSection(
    "Multi-Select Presets",
    "Array values, selected historical options, and permission-aware lists.",
  );
  const grid = createGrid();

  const type = createControlSlot("Task types", "Selected archived values remain visible.");
  addHandle(createTypeSelectPreset({
    root: type.root,
    appData: data,
    itemType: "task",
    value: ["clinical", "archived_type"],
    searchable: true,
  }));
  grid.append(type.field);

  const provider = createControlSlot("Providers", "Inactive selected provider remains visible.");
  addHandle(createProviderSelectPreset({
    root: provider.root,
    appData: data,
    value: ["provider_demo", "inactive_provider_demo"],
    searchable: true,
  }));
  grid.append(provider.field);

  const user = createControlSlot(
    "Permission-aware users",
    "Patient access: Alex Admin and Priya Provider. Project-only users remain visible only when already selected.",
  );
  addHandle(createUserSelectPreset({
    root: user.root,
    appData: data,
    name: "sandbox_patient_users",
    collectionName: "patients",
    value: ["admin_demo", "project_user_demo"],
    multiple: true,
    searchable: true,
  }));
  grid.append(user.field);

  const checkbox = createControlSlot(
    "Checkbox multiselect",
    "Shared-control checkbox menu with count display, bulk action, and enough options to prove internal scroll.",
  );
  addHandle(createSelectControl({
    root: checkbox.root,
    name: "sandbox_drug_export_filter",
    accessibleName: "Drug export filter",
    value: CHECKBOX_MULTISELECT_OPTIONS.map((option) => option.value),
    options: CHECKBOX_MULTISELECT_OPTIONS,
    multiple: true,
    searchable: true,
    searchThreshold: 7,
    checkboxMenu: true,
    bulkSelection: true,
    emptySelectionLabel: "None",
    allSelectedLabel: "All drugs",
    selectedCountLabel: "Selected drugs",
    searchPlaceholder: "Search Drug",
  }));
  grid.append(checkbox.field);

  section.append(grid);
  form.append(section);
}

function mountDateGallery(form) {
  const section = createSection(
    "Date Controls",
    "Date-only, datetime-local, range, disabled, and range validation states.",
  );
  const grid = createGrid();

  const dateOnly = createControlSlot("Date only");
  addHandle(createDateControl({
    root: dateOnly.root,
    name: "sandbox_due_date",
    type: "date",
    value: "2026-05-08",
    label: "Due date",
  }));
  grid.append(dateOnly.field);

  const datetime = createControlSlot("Datetime local");
  addHandle(createDateControl({
    root: datetime.root,
    name: "sandbox_next_appointment",
    type: "datetime-local",
    value: "2026-05-08T09:30",
    label: "Appointment",
  }));
  grid.append(datetime.field);

  const range = createControlSlot("Date range");
  addHandle(createDateRangePreset({
    root: range.root,
    name: "sandbox_date_range",
    value: {start: "2026-05-01", end: "2026-05-31"},
  }));
  grid.append(range.field);

  const invalidRange = createControlSlot(
    "Required date range",
    "Standard required state shows only the label asterisk until validation.",
    {required: true},
  );
  addHandle(createDateRangePreset({
    root: invalidRange.root,
    name: "sandbox_required_date_range",
    value: {start: "", end: ""},
    required: true,
  }));
  grid.append(invalidRange.field);

  section.append(grid);
  form.append(section);
}

function mountStandardFieldGallery(form) {
  const section = createSection(
    "Design System Fields",
    "Native-backed fields and helper states for approval alongside upgraded controls.",
  );
  const grid = createGrid();

  grid.append(createNativeField(
    "Text input",
    '<input type="text" value="Patient follow-up" class="h-[30px] w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]">',
    "Standard compact text field.",
  ));

  grid.append(createNativeField(
    "Required input",
    createRequiredInputExample(),
    "Standard required state shows only the label asterisk until validation.",
    {required: true},
  ));

  grid.append(createNativeField(
    "Disabled input",
    '<input type="text" value="Unavailable" disabled class="h-[30px] w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-100 px-2 py-1 text-sm text-gray-400">',
    "Disabled fields use neutral grey treatment.",
  ));

  grid.append(createNativeField(
    "Read-only field",
    '<p class="min-h-[30px] rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-700">Read-only display value</p>',
    "Read-only display follows current detail-field treatment.",
  ));

  grid.append(createNativeField(
    "Number input",
    '<input type="number" value="30" class="h-[30px] w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]">',
    "Native number input, compact app field height.",
  ));

  grid.append(createNativeField(
    "Currency field",
    createCurrencyFieldExample(),
    "Formats as currency on blur and accepts plain decimal entry on focus.",
  ));

  grid.append(createNativeField(
    "Compact integer field",
    createCompactIntegerExample(),
    "Small-width numeric field for two- or three-digit values.",
  ));

  grid.append(createNativeField(
    "Inline edit field",
    createInlineEditFieldExample(),
    "Header-style inline editing for compact labels and group names.",
  ));

  grid.append(createNativeField(
    "Helper text",
    createHelperTextExample(),
  ));

  section.append(grid);

  const textAreaGrid = createGrid();
  textAreaGrid.classList.add("mt-4");
  textAreaGrid.append(createNativeField(
    "Fixed textarea",
    '<textarea class="h-24 w-full resize-y rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]">Fixed-size long text with normal scrolling.</textarea>',
  ));
  textAreaGrid.append(createNativeField(
    "Auto-growing textarea",
    createAutoGrowingTextareaExample(),
  ));
  textAreaGrid.append(createNativeField(
    "Details overflow field",
    createDetailsOverflowExample(),
    "Editable capped long-text treatment with the current details overflow affordance.",
  ));

  section.append(textAreaGrid);
  form.append(section);
}

function mountChoiceAndActionGallery(form) {
  const section = createSection(
    "Design System Choices And Actions",
    "Native choices, app-owned actions, navigation, and row examples.",
  );
  const grid = createGrid();

  grid.append(createNativeField(
    "Checkbox",
    createCheckboxExample(),
  ));

  grid.append(createNativeField(
    "Checked-off checklist item",
    createCheckedOffChecklistExample(),
    "Completed checklist styling: checked control, muted text, and line-through label.",
  ));

  grid.append(createNativeField(
    "Radio group",
    createRadioGroupExample(),
  ));

  grid.append(createNativeField(
    "Primary button",
    '<button type="button" class="inline-flex h-9 items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-gray-900 transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none">Save</button>',
    "Primary brand colour for app-owned actions.",
  ));

  grid.append(createNativeField(
    "Secondary button",
    '<button type="button" class="inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-300 focus:outline-none">Cancel</button>',
    "Neutral secondary action stays non-brand.",
  ));

  grid.append(createNativeField(
    "Danger button",
    '<button type="button" class="inline-flex h-9 items-center justify-center rounded-md bg-red-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus:outline-none">Delete</button>',
    "Danger remains semantic red.",
  ));

  grid.append(createNativeField(
    "Icon navigation button",
    createIconNavigationExample(),
    "Navigation icon with brand hover circle and accent active state.",
  ));

  grid.append(createNativeField(
    "Special action icons",
    createIconButtonExamples(),
    "High-signal add and quick-close icon actions based on board, detail, and header patterns.",
  ));

  grid.append(createNativeField(
    "Standard action icons",
    createSpecialActionIconExamples(),
    "Low-chrome archive, delete, and recurring icons that change colour without brand fill.",
  ));

  grid.append(createNativeField(
    "Toggle switches",
    createToggleSetExample(),
  ));

  grid.append(createNativeField(
    "Sub menu navigation",
    createDesktopNavigationExample(),
    "Interactive sub-menu tab navigation based on controlled-drugs and settings tabs.",
  ));

  section.append(grid);
  form.append(section);
}

function mountMobileNavigationGallery(form) {
  const section = createSection("Mobile Navigation");
  const grid = createGrid();
  grid.append(createNativeField(
    "Mobile burger navigation",
    createMobileNavigationExample(),
    "Interactive mobile menu concept for replacing the main nav on small screens.",
  ));
  section.append(grid);
  form.append(section);
}

function mountTableGallery(form) {
  const section = createSection(
    "Design System Tables",
    "Fixed, controlled, and adjustable table examples.",
  );
  const listGrid = createGrid();
  const fixedTable = createNativeField(
    "Fixed table header",
    createFixedTableExample(),
    "Stock-table style with sticky first column, multiple location columns, and aligned totals.",
  );
  fixedTable.classList.add("lg:col-span-2");
  listGrid.append(fixedTable);

  const fixedControlStack = document.createElement("div");
  fixedControlStack.className = "space-y-4";
  fixedControlStack.append(
    createFixedTableWithControlsExample(),
    createCollisionProofViewport(
      createFixedTableWithControlsExample({initialOpenFilterKey: "date", compactFirstColumn: true}),
      "Restricted top/left viewport replica",
      {height: "220px", width: "520px", contentWidth: "560px", placement: "topLeft", left: "-90px"},
    ),
  );
  const fixedControlTable = createNativeField(
    "Fixed table with sort and filter",
    fixedControlStack,
    "Fixed table presentation with column-level sort/filter controls and constrained viewport collision proof.",
  );
  fixedControlTable.classList.add("lg:col-span-2");
  listGrid.append(fixedControlTable);

  const adjustableTable = createNativeField(
    "Adjustable table",
    createAdjustableTableExample(),
    "Drag a header to reorder columns, or drag the thin right edge to resize.",
  );
  adjustableTable.classList.add("lg:col-span-2");
  listGrid.append(adjustableTable);

  const adjustableControlStack = document.createElement("div");
  adjustableControlStack.className = "space-y-4";
  adjustableControlStack.append(
    createAdjustableTableExample({withControls: true}),
    createCollisionProofViewport(
      createAdjustableTableExample({withControls: true, initialOpenFilterKey: "location"}),
      "Restricted bottom/right viewport replica",
      {height: "220px", width: "360px", contentWidth: "560px", placement: "bottomRight", bottom: "-100px"},
    ),
  );
  const filterKindProof = document.createElement("div");
  filterKindProof.className = "space-y-2";
  const filterKindProofCaption = document.createElement("p");
  filterKindProofCaption.className = "text-xs font-medium text-gray-500";
  filterKindProofCaption.textContent = "Filter type proof replica";
  filterKindProof.append(filterKindProofCaption, createFilterKindTableExample());
  adjustableControlStack.append(filterKindProof);
  const adjustableControlTable = createNativeField(
    "Adjustable table with sort and filter",
    adjustableControlStack,
    "Adjustable table with column movement, resizing, sort/filter controls, and constrained viewport collision proof.",
  );
  adjustableControlTable.classList.add("lg:col-span-2");
  listGrid.append(adjustableControlTable);

  section.append(listGrid);
  form.append(section);
}

function mountGallery(form) {
  const data = getSandboxData();
  mountSelectGallery(form, data);
  mountTextOptionGallery(form);
  mountMultiSelectGallery(form, data);
  mountDateGallery(form);
  mountStandardFieldGallery(form);
  mountChoiceAndActionGallery(form);
  mountBoardMenuGallery(form);
  mountMobileNavigationGallery(form);
  mountTableGallery(form);
  bindSnapshotUpdates(form);
  updateSnapshot(form);
}

function renderSandboxShell(container) {
  applyBrandingVariables(container, appData);
  container.classList.add("bg-white");
  container.innerHTML = `
    <section class="mx-auto flex max-w-6xl flex-col gap-6 bg-white">
      <div class="flex flex-col gap-2 border-b border-gray-200 pb-4">
        <p class="text-sm font-semibold uppercase tracking-normal text-gray-500">
          Hidden development route
        </p>
        <div class="flex items-center justify-between gap-3 md:block">
          <h2 class="text-2xl font-semibold text-gray-900">Controls Sandbox</h2>
          ${renderMobileRouteHeaderActions()}
        </div>
      </div>

      <form id="controls-sandbox-form" class="space-y-6"></form>

      <section class="space-y-3">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 class="text-base font-semibold text-gray-900">Registered Values</h3>
          <button
            type="button"
            id="sandbox-validate-btn"
            class="inline-flex items-center justify-center rounded-lg bg-[var(--dfc-brand-primary)] px-3 py-2 text-sm font-semibold text-gray-900 transition-colors hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
          >
            <span class="material-symbols-outlined mr-1 text-[18px]" aria-hidden="true">rule</span>
            Validate
          </button>
        </div>
        <pre
          id="sandbox-values-output"
          class="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-gray-950 p-4 text-xs leading-relaxed text-gray-100"
        ></pre>
      </section>
    </section>
  `;
}

/**
 * Renders the temporary controls sandbox shell and mounted gallery examples.
 */
export function renderControlsSandboxView() {
  const container = document.getElementById("sandbox-view");
  if (!container) {
    console.error("Controls sandbox container (#sandbox-view) not found.");
    return;
  }

  destroyRegisteredControlsIn(container);
  activeHandles.forEach((handle) => handle.destroy?.());
  activeHandles = [];
  nextReviewItemNumber = 1;

  renderSandboxShell(container);

  const form = document.getElementById("controls-sandbox-form");
  if (!form) {
    console.error("Controls sandbox form not found.");
    return;
  }

  mountGallery(form);
}
